<template>
  <div v-if="isActive" :class="['video-chat-container', { 'minimized': isMinimized, 'expanded': !isMinimized, 'fullscreen': isFullscreen, 'sharing-with-participants': isMinimized && isScreenSharing && participants.length > 0 }]">
    <!-- Header -->
    <div class="video-chat-header" @click="toggleMinimize">
      <div class="header-content">
        <v-icon small class="mr-2">fas fa-video</v-icon>
        <span class="header-title">Video Call ({{ participantCount }} {{ participantLabel }})</span>
        
        <!-- Audio enable button (when blocked by autoplay) -->
        <v-btn
          v-if="audioPlaybackBlocked"
          x-small
          color="warning"
          class="ml-2"
          @click.stop="enableAudioPlayback"
        >
          <v-icon x-small class="mr-1">fas fa-volume-up</v-icon>
          Enable Audio
        </v-btn>
        
        <!-- Screen sharing indicator -->
        <div v-if="isScreenSharing" class="screen-share-indicator" @click.stop>
          <v-icon small color="primary" class="mr-1">fas fa-desktop</v-icon>
          <span class="share-text">Sharing Screen</span>
          <v-btn
            x-small
            color="error"
            class="ml-2 stop-share-btn"
            @click.stop="toggleScreenShare"
          >
            Stop Sharing
          </v-btn>
        </div>
        
        <!-- Recording indicator -->
        <div v-if="isRecording" class="recording-indicator" @click.stop>
          <div class="recording-dot"></div>
          <span class="recording-text">REC {{ formatRecordingDuration(recordingDuration) }}</span>
          <span v-if="recordingPartsCount > 0" class="recording-parts">Part {{ recordingPartsCount }}</span>
        </div>
      </div>
      <div class="header-actions">
        <v-btn icon x-small @click.stop="toggleMinimize">
          <v-icon small>{{ isMinimized ? 'fas fa-chevron-up' : 'fas fa-chevron-down' }}</v-icon>
        </v-btn>
        <v-btn icon x-small @click.stop="endCall">
          <v-icon small>fas fa-times</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Fullscreen Top Bar - show previews of all videos -->
    <div v-show="isFullscreen && !isMinimized && !showSettings" class="fullscreen-top-bar">
      <!-- Your camera preview (only if not focused) -->
      <div 
        v-show="!(focusedVideo && focusedVideo.type === 'local' && focusedVideo.streamType === 'camera')"
        class="video-preview-tile" 
        @click="focusVideo('local', null, 'camera')"
      >
        <video
          ref="fullscreenLocalVideo"
          autoplay
          playsinline
          muted
          class="video-element"
          v-show="!localMediaError"
        ></video>
        <div v-if="localMediaError" class="video-error-overlay">
          <v-icon small color="red">fas fa-exclamation-triangle</v-icon>
        </div>
        <div class="video-overlay">
          <div class="participant-name-small">You</div>
        </div>
      </div>
      
      <!-- Your screen preview (when screen sharing, only if not focused) -->
      <div 
        v-show="isScreenSharing && !(focusedVideo && focusedVideo.type === 'local' && focusedVideo.streamType === 'screen')"
        class="video-preview-tile" 
        @click="focusVideo('local', null, 'screen')"
      >
        <video
          ref="fullscreenLocalScreenPreview"
          autoplay
          playsinline
          muted
          class="video-element"
        ></video>
        <div class="video-overlay">
          <div class="participant-name-small">You (Screen) 🖥️</div>
        </div>
      </div>
      
      <!-- Remote participants' camera previews (only if not focused) -->
      <div 
        v-for="participant in participants" 
        :key="'preview-camera-' + participant.sessionId" 
        class="video-preview-tile"
        :class="{ 'no-camera': !participant.cameraStream }"
        v-show="participant.cameraStream && !(focusedVideo && focusedVideo.type === 'remote' && focusedVideo.sessionId === participant.sessionId && focusedVideo.streamType === 'camera')"
        @click="focusVideo('remote', participant.sessionId, 'camera')"
      >
        <video
          :ref="'remoteCameraPreview-' + participant.sessionId"
          autoplay
          playsinline
          class="video-element"
        ></video>
        <div class="video-overlay">
          <div class="participant-name-small">
            {{ participant.userName }}
            <v-icon v-if="presentingUserId && participant.userId === presentingUserId" x-small color="amber">mdi-presentation-play</v-icon>
          </div>
          <div class="participant-controls">
            <v-btn
              icon
              x-small
              @click.stop="toggleParticipantMute(participant)"
              class="participant-control-btn"
              :title="participant.muted ? 'Unmute' : 'Mute'"
            >
              <v-icon x-small :color="participant.muted ? 'red' : 'white'">
                {{ participant.muted ? 'fas fa-volume-mute' : 'fas fa-volume-up' }}
              </v-icon>
            </v-btn>
          </div>
        </div>
      </div>
      
      <!-- Remote participants' screen previews (when sharing, only if not focused) -->
      <div 
        v-for="participant in participants" 
        :key="'preview-screen-' + participant.sessionId" 
        class="video-preview-tile screen-share-preview"
        v-show="participant.screenStream && !(focusedVideo && focusedVideo.type === 'remote' && focusedVideo.sessionId === participant.sessionId && focusedVideo.streamType === 'screen')"
        @click="focusVideo('remote', participant.sessionId, 'screen')"
      >
        <video
          :ref="'remoteScreenPreview-' + participant.sessionId"
          autoplay
          playsinline
          class="video-element"
        ></video>
        <div class="video-overlay">
          <div class="participant-name-small">{{ participant.userName }} (Screen) 🖥️</div>
          <div class="participant-controls">
            <v-btn
              icon
              x-small
              @click.stop="toggleParticipantMute(participant)"
              class="participant-control-btn"
              :title="participant.muted ? 'Unmute' : 'Mute'"
            >
              <v-icon x-small :color="participant.muted ? 'red' : 'white'">
                {{ participant.muted ? 'fas fa-volume-mute' : 'fas fa-volume-up' }}
              </v-icon>
            </v-btn>
          </div>
        </div>
      </div>
    </div>

    <!-- Video Grid -->
    <div v-show="!isMinimized && !showSettings" class="video-grid" :class="{ 'fullscreen-focused': isFullscreen && focusedVideo }">
      <!-- Debug info -->
      <div v-if="!localStream" style="color: white; padding: 12px; grid-column: 1 / -1;">
        Initializing camera...
      </div>

      <!-- Fullscreen mode: Show only the focused video -->
      <template v-if="isFullscreen && focusedVideo">
        <!-- Focused local camera -->
        <div v-if="focusedVideo.type === 'local' && focusedVideo.streamType === 'camera'" class="video-item fullscreen-focused-video local-video">
          <video
            ref="gridLocalVideo"
            autoplay
            playsinline
            muted
            class="video-element"
            v-show="!localMediaError"
          ></video>
          <div v-if="localMediaError" class="video-error-overlay">
            <v-icon large color="red">fas fa-exclamation-triangle</v-icon>
            <div class="error-text">{{ localMediaError }}</div>
          </div>
          <div class="video-overlay">
            <div class="participant-name">You</div>
          </div>
        </div>
        
        <!-- Focused local screen -->
        <div v-if="focusedVideo.type === 'local' && focusedVideo.streamType === 'screen'" class="video-item fullscreen-focused-video">
          <video
            ref="gridLocalScreen"
            autoplay
            playsinline
            muted
            class="video-element"
          ></video>
          <div class="video-overlay">
            <div class="participant-name">Your Screen</div>
          </div>
        </div>
        
        <!-- Focused remote camera -->
        <div v-if="focusedVideo.type === 'remote' && focusedVideo.streamType === 'camera'" class="video-item fullscreen-focused-video">
          <video
            :ref="'remoteVideo-' + focusedVideo.sessionId"
            autoplay
            playsinline
            class="video-element"
          ></video>
          <div class="video-overlay">
            <div class="participant-name">
              {{ getFocusedParticipantName() }}
            </div>
          </div>
        </div>
        
        <!-- Focused remote screen -->
        <div v-if="focusedVideo.type === 'remote' && focusedVideo.streamType === 'screen'" class="video-item fullscreen-focused-video">
          <video
            :ref="'remoteScreen-' + focusedVideo.sessionId"
            autoplay
            playsinline
            class="video-element"
          ></video>
          <div class="video-overlay">
            <div class="participant-name">
              {{ getFocusedParticipantName() }} (Screen)
            </div>
          </div>
        </div>
      </template>

      <!-- Non-fullscreen mode: Show all videos in grid -->
      <template v-if="!isFullscreen">
        <!-- Remote streams - show screen if sharing, otherwise camera -->
        <!-- Only render video elements for participants with active streams -->
        <div 
          v-for="participant in participants" 
          :key="participant.sessionId" 
          v-show="participant.cameraStream || participant.screenStream"
          class="video-item"
        >
          <video
            :ref="'remoteVideo-' + participant.sessionId"
            autoplay
            playsinline
            class="video-element"
          ></video>
          <div class="video-overlay">
            <div class="participant-name">
              {{ participant.userName }}{{ participant.screenStream ? ' (Screen)' : '' }}
              <v-icon v-if="presentingUserId && participant.userId === presentingUserId" small color="amber">mdi-presentation-play</v-icon>
            </div>
            <div class="participant-controls">
              <v-btn
                icon
                x-small
                @click.stop="toggleParticipantMute(participant)"
                class="participant-control-btn"
                :title="participant.muted ? 'Unmute' : 'Mute'"
              >
                <v-icon x-small :color="participant.muted ? 'red' : 'white'">
                  {{ participant.muted ? 'fas fa-volume-mute' : 'fas fa-volume-up' }}
                </v-icon>
              </v-btn>
            </div>
          </div>
        </div>

        <!-- Local camera feed -->
        <div class="video-item local-video">
          <video
            ref="gridLocalVideo"
            autoplay
            playsinline
            muted
            class="video-element"
            v-show="!localMediaError"
          ></video>
          <div v-if="localMediaError" class="video-error-overlay">
            <v-icon large color="red">fas fa-exclamation-triangle</v-icon>
            <div class="error-text">{{ localMediaError }}</div>
          </div>
          <div class="video-overlay">
            <div class="participant-name">You{{ isScreenSharing ? ' (Camera)' : '' }}</div>
          </div>
        </div>
      </template>
    </div>

    <!-- Settings View -->
    <div v-show="!isMinimized && showSettings" class="settings-view">
      <h3 class="white--text mb-4">Settings</h3>
      
      <!-- Show message if there's a media error -->
      <div v-if="localMediaError" class="alert-box mb-4">
        <v-icon small color="warning" class="mr-2">fas fa-exclamation-triangle</v-icon>
        <span class="alert-text">{{ localMediaError }}</span>
        <p class="alert-subtext">Try selecting a different device below or close other apps using your camera/microphone</p>
      </div>
      
      <div class="setting-group mb-4">
        <label class="white--text d-block mb-2">Microphone</label>
        <select v-model="selectedAudioDevice" @change="changeAudioDevice" class="device-select">
          <option v-for="device in audioDevices" :key="device.deviceId" :value="device.deviceId">
            {{ device.label || 'Microphone ' + (audioDevices.indexOf(device) + 1) }}
          </option>
        </select>
      </div>

      <div class="setting-group mb-4">
        <label class="white--text d-block mb-2">Camera</label>
        <select 
          v-model="selectedVideoDevice" 
          @change="changeVideoDevice" 
          class="device-select"
          :disabled="isScreenSharing"
          :title="isScreenSharing ? 'Cannot change camera while screen sharing' : ''"
        >
          <option v-for="device in videoDevices" :key="device.deviceId" :value="device.deviceId">
            {{ device.label || 'Camera ' + (videoDevices.indexOf(device) + 1) }}
          </option>
        </select>
      </div>

      <div class="setting-group mb-4">
        <label class="white--text d-block mb-2">Speaker (Audio Output)</label>
        <select 
          v-model="selectedAudioOutputDevice" 
          @change="changeAudioOutputDevice" 
          class="device-select"
        >
          <option v-for="device in audioOutputDevices" :key="device.deviceId" :value="device.deviceId">
            {{ device.label || 'Speaker ' + (audioOutputDevices.indexOf(device) + 1) }}
          </option>
        </select>
      </div>
      
      <div class="text-center mt-4">
        <v-btn small color="primary" @click="toggleSettings">Done</v-btn>
      </div>
    </div>

    <!-- Controls -->
    <div v-show="!isMinimized" class="video-controls">
      <v-btn
        icon
        :color="audioEnabled ? 'white' : 'red'"
        @click="toggleAudio"
        class="control-btn"
      >
        <v-icon>{{ audioEnabled ? 'fas fa-microphone' : 'fas fa-microphone-slash' }}</v-icon>
      </v-btn>
      
      <v-btn
        icon
        :color="videoEnabled ? 'white' : 'red'"
        @click="toggleVideo"
        class="control-btn"
      >
        <v-icon>{{ videoEnabled ? 'fas fa-video' : 'fas fa-video-slash' }}</v-icon>
      </v-btn>
      
      <v-btn
        icon
        :color="speakerEnabled ? 'white' : 'red'"
        @click="toggleSpeaker"
        class="control-btn"
        :title="speakerEnabled ? 'Mute All Speakers' : 'Unmute All Speakers'"
      >
        <v-icon>{{ speakerEnabled ? 'fas fa-volume-up' : 'fas fa-volume-mute' }}</v-icon>
      </v-btn>
      
      <v-btn
        icon
        :color="isScreenSharing ? 'primary' : 'white'"
        @click="toggleScreenShare"
        class="control-btn"
      >
        <v-icon>fas fa-desktop</v-icon>
      </v-btn>
      
      <v-btn
        icon
        :color="isRecording ? 'error' : 'white'"
        @click="isRecording ? stopRecording() : startRecording()"
        class="control-btn"
        :title="isRecording ? 'Stop Recording' : 'Start Recording'"
      >
        <v-icon>{{ isRecording ? 'fas fa-stop' : 'fas fa-circle' }}</v-icon>
      </v-btn>

      <v-btn
        icon
        :color="showSettings ? 'primary' : 'white'"
        @click="toggleSettings"
        class="control-btn"
      >
        <v-icon>fas fa-cog</v-icon>
      </v-btn>

      <v-btn
        v-show="!isMinimized"
        icon
        :color="isFullscreen ? 'primary' : 'white'"
        @click="toggleFullscreen"
        class="control-btn"
      >
        <v-icon>{{ isFullscreen ? 'fas fa-compress' : 'fas fa-expand' }}</v-icon>
      </v-btn>

      <v-btn
        icon
        color="red"
        @click="endCall"
        class="control-btn hang-up"
      >
        <v-icon>fas fa-phone-slash</v-icon>
      </v-btn>
    </div>

    <!-- Minimized Preview -->
    <div v-show="isMinimized" class="minimized-preview">
      <!-- A remote participant is sharing their screen: show it so the minimized
           window still surfaces what they're presenting -->
      <template v-if="remoteScreenShareParticipant && !isScreenSharing">
        <video
          ref="minimizedRemoteScreen"
          autoplay
          playsinline
          class="minimized-screen-video"
        ></video>
        <div class="minimized-participant-name minimized-screen-label">
          {{ remoteScreenShareParticipant.userName }}'s screen
        </div>
      </template>

      <!-- Show remote participants when screen sharing -->
      <template v-else-if="isScreenSharing && participants.length > 0">
        <div class="minimized-participants-grid">
          <div 
            v-for="participant in participants" 
            :key="'minimized-' + participant.sessionId"
            class="minimized-participant-tile"
          >
            <video
              :ref="'minimizedParticipant-' + participant.sessionId"
              autoplay
              playsinline
              class="minimized-participant-video"
            ></video>
            <div class="minimized-participant-name">
              {{ participant.userName }}
            </div>
          </div>
        </div>
      </template>
      
      <!-- Show single video when not screen sharing (original behavior) -->
      <template v-else>
        <video
          ref="minimizedVideo"
          autoplay
          playsinline
          muted
          class="minimized-video"
        ></video>
      </template>
    </div>
  </div>
</template>

<script>
import webrtcService from '../service/webrtc-service';

export default {
  name: 'VideoChat',
  props: {
    vaultId: {
      type: String,
      required: true
    },
    userId: {
      type: String,
      required: true
    },
    activeUsers: {
      type: Array,
      default: () => []
    },
    // User id of the active "present this file" presenter (null when nobody is presenting)
    presentingUserId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      isActive: false,
      isMinimized: false,
      audioEnabled: true,
      videoEnabled: true,
      speakerEnabled: true, // Global speaker toggle for all incoming audio
      isScreenSharing: false,
      participants: [], // Array of {sessionId, userId, userName, cameraStream, screenStream, muted: false, volume: 1.0}
      localStream: null,
      isLocalStreamReady: false,
      connectedPeers: new Set(), // Track peers we've already connected to (by sessionId)
      showSettings: false,
      audioDevices: [],
      videoDevices: [],
      audioOutputDevices: [],
      selectedAudioDevice: null,
      selectedVideoDevice: null,
      selectedAudioOutputDevice: null,
      localMediaError: null,
      isFullscreen: false,
      pseudoFullscreen: false, // CSS-only fullscreen fallback for browsers without the element Fullscreen API (iOS Safari)
      connectionDiagnostics: null, // For debugging connection issues
      focusedVideo: null, // Track which video is focused in fullscreen: { type: 'local'|'remote', sessionId?: string, streamType: 'camera'|'screen' }
      isAttachingStreams: false, // Guard to prevent rapid re-attachment
      audioPlaybackBlocked: false, // Track if audio is blocked by browser autoplay policy
      // Recording properties
      isRecording: false,
      mediaRecorder: null,
      s3Manager: null, // S3 instance for multipart uploads
      uploadedParts: [], // Track uploaded parts [{PartNumber, ETag}]
      recordingStartTime: null,
      recordingTimer: null,
      recordingFileId: null,
      recordingUploadId: null,
      recordingFilePath: null,
      currentPartNumber: 0,
      chunkBuffer: [], // Buffer for small chunks
      bufferSize: 0, // Track buffer size in bytes
      recordingDuration: 0, // Duration in seconds
      recordingPartsCount: 0, // Number of parts uploaded
      compositeCanvas: null, // Canvas for composite stream
      compositeContext: null, // Canvas 2D context
      compositeAnimationId: null, // requestAnimationFrame ID
      compositeStream: null, // MediaStream from canvas
      audioContext: null, // Web Audio API context for mixing
      audioDestination: null, // Audio destination node
      recordingError: null, // Track recording errors
      isDrawingComposite: false // Track if composite drawing is active
    };
  },
  computed: {
    participantCount() {
      let count = this.participants.length;
      if (this.isActive && this.isLocalStreamReady) {
        count += 1;
      }
      return count;
    },
    participantLabel() {
      return this.participantCount === 1 ? 'person' : 'people';
    },
    // First remote participant currently sharing their screen (used to show
    // their screen in the minimized preview so "what they're sharing" stays
    // visible after the window auto-minimizes).
    remoteScreenShareParticipant() {
      return this.participants.find(p => p.screenStream) || null;
    }
  },
  methods: {
    resetPeerState() {
      this.participants = [];
      this.connectedPeers = new Set();
    },

    stopLocalMedia() {
      if (this.localStream) {
        this.localStream.getTracks().forEach(track => track.stop());
        this.localStream = null;
      }
    },

    async startLocalStreamWithFallback() {
      // Try with selected devices first
      if (this.selectedAudioDevice && this.selectedVideoDevice) {
        try {
          console.log('🎯 Attempting with selected devices...');
          return await webrtcService.startLocalStream(this.selectedAudioDevice, this.selectedVideoDevice);
        } catch (error) {
          if (error.name === 'NotReadableError') {
            console.log('⚠️ Selected devices in use, trying alternatives...');
            // Continue to fallback logic
          } else {
            throw error; // Re-throw other errors
          }
        }
      }
      
      // Auto-fallback: Try different device combinations
      const devices = await webrtcService.getMediaDevices();
      console.log(`🔄 Auto-fallback: Found ${devices.audio.length} audio and ${devices.video.length} video devices`);
      
      if (devices.video.length === 0 || devices.audio.length === 0) {
        const error = new Error('No camera or microphone devices found');
        error.name = 'NotFoundError';
        throw error;
      }
      
      // Try each combination until one works
      let attemptCount = 0;
      const totalCombinations = devices.video.length * devices.audio.length;
      
      for (const videoDevice of devices.video) {
        for (const audioDevice of devices.audio) {
          attemptCount++;
          try {
            console.log(`  [${attemptCount}/${totalCombinations}] Trying: ${videoDevice.label || 'Video'} + ${audioDevice.label || 'Audio'}...`);
            const stream = await webrtcService.startLocalStream(audioDevice.deviceId, videoDevice.deviceId);
            
            // Success! Update selected devices for Settings UI
            this.selectedAudioDevice = audioDevice.deviceId;
            this.selectedVideoDevice = videoDevice.deviceId;
            
            console.log(`✅ Successfully started stream with: ${videoDevice.label || 'Video device'} + ${audioDevice.label || 'Audio device'}`);
            return stream;
          } catch (error) {
            console.log(`  ✗ Failed: ${error.name} - ${error.message}`);
            if (error.name === 'NotReadableError') {
              console.log(`  → Device combination in use, trying next...`);
              continue; // Try next combination
            } else {
              // Other errors (permission denied, not found, etc.) should stop trying
              console.error(`  ❌ Stopping fallback due to non-recoverable error: ${error.name}`);
              throw error;
            }
          }
        }
      }
      
      // If we get here, all combinations failed
      console.log('❌ All device combinations failed');
      const error = new Error(`All ${devices.video.length * devices.audio.length} camera/microphone combinations are in use or unavailable`);
      error.name = 'NotReadableError';
      throw error;
    },

    async startCall() {
      try {
        this.resetPeerState();
        this.isMinimized = false;
        this.isScreenSharing = false;

        // Set isActive first so video elements are rendered
        this.isActive = true;
        
        // Initialize WebRTC service
        console.log(`Initializing WebRTC for Vault: ${this.vaultId}, User: ${this.userId}`);
        await webrtcService.initialize(this.vaultId, this.userId, {
          onRemoteStream: this.handleRemoteStream,
          onRemoteStreamRemoved: this.handleRemoteStreamRemoved,
          onConnectionState: this.handleConnectionState,
          onScreenShareStopped: this.handleScreenShareStopped,
          onPresentMessage: this.handlePresentMessage,
          onPresentChannelOpen: this.handlePresentChannelOpen
        });

        // Start local media stream with auto-fallback for busy devices
        try {
          this.localMediaError = null;
          this.localStream = await this.startLocalStreamWithFallback();
          
          // Wait for DOM to be ready, then attach local stream to video elements
          await this.$nextTick();
          
          console.log('Attaching local stream to video elements...');
          console.log('Local stream tracks:', this.localStream.getTracks());
          
          // Attach to all local video elements (they may not all exist depending on state)
          this.attachLocalStreamToVideoElements();
        } catch (mediaError) {
          console.warn('Failed to start local media, proceeding without it:', mediaError);
          this.localStream = null;
          this.audioEnabled = false;
          this.videoEnabled = false;
          
          let errorMessage = 'Could not access camera/microphone.';
          let shortError = 'Camera unavailable';
          
          // Check for specific error types to give better messages
          if (mediaError.name === 'NotReadableError' || (mediaError.message && mediaError.message.includes('in use'))) {
            errorMessage = 'All cameras/microphones are in use. Please close other apps or open Settings to select a different device.';
            shortError = 'Device in use';
          } else if (mediaError.name === 'NotAllowedError') {
            errorMessage = 'Camera/microphone access denied. Joining as listener.';
            shortError = 'Access denied';
          } else if (mediaError.name === 'NotFoundError') {
            errorMessage = 'No camera/microphone found. Joining as listener.';
            shortError = 'No device found';
          }
          
          this.localMediaError = shortError;
          this.$emit('show-error', errorMessage);
          
          // Listener mode: mark the service ready without local media so
          // queued incoming offers are processed and receive-only
          // connections can be established
          await webrtcService.markLocalStreamReady();
        }

        // Mark local stream as ready BEFORE connecting to peers
        this.isLocalStreamReady = true;
        console.log('Local stream ready (or skipped), now connecting to peers...');

        // Connect to existing participants (filter by session_id to allow same user's other sessions)
        const mySessionId = webrtcService.getSessionId();
        const otherSessions = this.activeUsers.filter(u => u.session_id && u.session_id !== mySessionId);
        console.log('Other sessions found:', otherSessions.length, otherSessions);
        for (const session of otherSessions) {
          // For initiating connections, we use user_id (signals go to all their sessions)
          // But track by user_id to avoid duplicate connection attempts
          if (!this.connectedPeers.has(session.user_id)) {
            if (!this.shouldInitiateConnectionTo(session.session_id)) {
              console.log('Waiting for offer from peer session (they initiate):', session.session_id, 'user:', session.user_id);
              continue;
            }
            console.log('Connecting to peer session:', session.session_id, 'user:', session.user_id);
            this.connectedPeers.add(session.user_id);
            await webrtcService.connectToPeer(session.user_id);
          }
        }

        this.$emit('call-started');
      } catch (error) {
        console.error('Error starting call:', error);
        try {
          await webrtcService.cleanup();
        } catch (cleanupError) {
          console.error('Error during cleanup after failed start:', cleanupError);
        }
        this.stopLocalMedia();
        this.resetPeerState();
        this.isActive = false;
        this.isLocalStreamReady = false;
        this.$emit('call-error', error);
      }
    },

    async endCall() {
      // Check if recording is active
      if (this.isRecording) {
        const confirmed = confirm('Recording is in progress. Ending the call will stop and save the recording. Continue?');
        if (!confirmed) {
          return;
        }
        // Stop recording first
        await this.stopRecording();
        // Wait a bit for recording to finalize
        await new Promise(resolve => setTimeout(resolve, 2000));
      }

      let cleanupError = null;
      try {
        await webrtcService.cleanup();
      } catch (error) {
        cleanupError = error;
        console.error('Error ending call:', error);
      } finally {
        this.stopLocalMedia();
        this.resetPeerState();
        this.cleanupRecording(); // Ensure recording cleanup
        this.isActive = false;
        this.isLocalStreamReady = false;
        this.isScreenSharing = false;
        this.$emit('call-ended');
      }

      if (cleanupError) {
        this.$emit('call-error', cleanupError);
      }
    },

    handleRemoteStream(sessionId, stream, isScreenTrack = false) {
      console.log('🎬 handleRemoteStream called for sessionId:', sessionId);
      console.log(`Stream: ${stream ? stream.id : 'null'}, Tracks: ${stream ? stream.getTracks().length : 0}, Type: ${isScreenTrack ? 'SCREEN' : 'CAMERA'}`);
      
      // Extract userId from sessionId (format: userId_timestamp_random)
      const userId = sessionId.split('_')[0];
      
      // Don't show our own other sessions in the participant list
      if (userId === this.userId) {
        console.log('🔇 Ignoring stream from own user (different device)');
        return;
      }
      
      // Find participant by sessionId, or fallback to finding by userId
      let participant = this.participants.find(p => p.sessionId === sessionId);
      if (!participant) {
        // Try to find by userId as fallback (in case sessionId changed during reconnection)
        participant = this.participants.find(p => p.userId === userId);
        if (participant) {
          console.log(`ℹ️ Found participant by userId instead of sessionId (${participant.sessionId} vs ${sessionId})`);
        }
      }
      
      // If stream is null (track ended), only update existing participants
      // Don't create a new participant for a null stream
      if (stream === null) {
        if (!participant) {
          console.log('⚠️ Received null stream for non-existent participant (userId:', userId, 'sessionId:', sessionId, ')');
          console.log('   Current participants:', this.participants.map(p => ({ userId: p.userId, sessionId: p.sessionId })));
          return;
        }
        
        // Update participant's sessionId if we found them by userId but have a more specific sessionId
        // This can happen when the WebRTC connection was initially created with userId but later resolved to full sessionId
        if (participant.sessionId !== sessionId && sessionId.startsWith(userId + '_')) {
          console.log(`🔄 Updating participant sessionId from ${participant.sessionId} to ${sessionId}`);
          participant.sessionId = sessionId;
        }
        
        // Clear the appropriate stream
        if (isScreenTrack) {
          console.log('🛑 Clearing participant screen stream (screen sharing stopped):', participant.sessionId);
          participant.screenStream = null;
        } else {
          console.log('🛑 Clearing participant camera stream (camera ended):', participant.sessionId);
          participant.cameraStream = null;
        }
        
        // Attach streams (this will switch back to camera if screen was cleared)
        // Use the sessionId that was passed in (it's the most current one from WebRTC)
        this.$nextTick(() => {
          console.log(`🔄 Reattaching streams for participant ${participant.userName} (using sessionId: ${sessionId})`);
          this.attachParticipantStreams(sessionId);
        });
        return;
      }
      
      // For non-null streams, create participant if they don't exist
      if (!participant) {
        const user = this.activeUsers.find(u => u.user_id === userId);
        console.log('➕ Adding new participant:', sessionId, user ? user.user_name : 'Unknown');
        participant = {
          sessionId: sessionId,
          userId: userId,
          userName: user ? user.user_name : 'Unknown User',
          cameraStream: null,
          screenStream: null,
          muted: false,
          volume: 1.0
        };
        this.participants.push(participant);
        console.log('Total participants now:', this.participants.length);
      } else {
        // Update participant's sessionId if we found them by userId but have a more specific sessionId
        // This ensures the sessionId stays synchronized with WebRTC's actual peer connection key
        if (participant.sessionId !== sessionId && sessionId.startsWith(userId + '_')) {
          console.log(`🔄 Updating participant sessionId from ${participant.sessionId} to ${sessionId}`);
          participant.sessionId = sessionId;
        }
      }
      
      // Update appropriate stream based on track type
      if (isScreenTrack) {
        const isNewScreenShare = !participant.screenStream || participant.screenStream.id !== stream.id;
        console.log('🔄 Updating participant screen stream:', sessionId);
        participant.screenStream = stream;
        // A remote participant just started sharing - auto-minimize so the
        // shared screen is surfaced and the call UI gets out of the way.
        if (isNewScreenShare && !this.isMinimized) {
          console.log('📉 Auto-minimizing for incoming screen share from', participant.userName);
          this.isMinimized = true;
          this.showSettings = false;
        }
      } else {
        // Camera audio + video arrive in the same remote MediaStream (the
        // sender's stream id propagates via SDP msid), so no manual track
        // merging is needed - just keep the stream reference current
        if (!participant.cameraStream || participant.cameraStream.id !== stream.id) {
          console.log('🔄 Setting camera stream for:', sessionId);
          participant.cameraStream = stream;
        } else {
          // Same stream object - the browser adds new tracks to it automatically
          console.log(`🔄 Camera stream unchanged for ${sessionId} (${stream.getTracks().map(t => t.kind).join(', ')})`);
        }
      }

      // Auto-focus on screen shares when they start (if in fullscreen)
      if (this.isFullscreen && isScreenTrack && stream && !this.focusedVideo) {
        console.log('🎯 Auto-focusing on new screen share from', participant.userName);
        this.focusVideo('remote', sessionId, 'screen');
      } else if (this.isFullscreen && isScreenTrack && stream && this.focusedVideo) {
        // If we're already focused on something, check if we should switch to this screen share
        const currentlyFocusedParticipant = this.focusedVideo.type === 'remote' 
          ? this.participants.find(p => p.sessionId === this.focusedVideo.sessionId)
          : null;
        
        // Switch to new screen share if current focus is on camera or local
        if (this.focusedVideo.type === 'local' || 
            (this.focusedVideo.streamType === 'camera' && currentlyFocusedParticipant)) {
          console.log('🎯 Switching focus to new screen share from', participant.userName);
          this.focusVideo('remote', sessionId, 'screen');
        }
      }
      // Note: participants watcher will trigger attachAllStreams automatically
    },
    
    attachParticipantStreams(sessionId) {
      // Try to find by exact sessionId first
      let participant = this.participants.find(p => p.sessionId === sessionId);
      
      // If not found, try finding by userId (extract from sessionId)
      if (!participant) {
        const userId = sessionId.split('_')[0];
        participant = this.participants.find(p => p.userId === userId);
        
        if (participant) {
          console.log(`ℹ️ Found participant by userId ${userId} instead of exact sessionId ${sessionId}`);
          // Update the participant's sessionId to the current one
          console.log(`   Updating participant sessionId from ${participant.sessionId} to ${sessionId}`);
          participant.sessionId = sessionId;
        }
      }
      
      if (!participant) {
        console.warn('⚠️ Participant not found for sessionId:', sessionId);
        return;
      }
      
      // In non-fullscreen grid: Show screen if available, otherwise camera
      if (!this.isFullscreen) {
        const gridStreamToShow = participant.screenStream || participant.cameraStream;
        const refName = 'remoteVideo-' + sessionId;
        const videoRef = this.$refs[refName];
        
        if (gridStreamToShow && videoRef) {
          this.safeSetVideoSource(
            videoRef,
            gridStreamToShow,
            `remoteVideo-${participant.userName}`
          );
        } else if (!videoRef) {
          console.warn(`⚠️ Grid video ref not found: ${refName}`);
        }
      }
      
      // In fullscreen mode: Attach focused video if this is the focused participant
      if (this.isFullscreen && this.focusedVideo && this.focusedVideo.type === 'remote' && this.focusedVideo.sessionId === sessionId) {
        if (this.focusedVideo.streamType === 'camera' && participant.cameraStream) {
          this.safeSetVideoSource(
            this.$refs['remoteVideo-' + sessionId],
            participant.cameraStream,
            `remoteFocusedCamera-${participant.userName}`
          );
        } else if (this.focusedVideo.streamType === 'screen' && participant.screenStream) {
          this.safeSetVideoSource(
            this.$refs['remoteScreen-' + sessionId],
            participant.screenStream,
            `remoteFocusedScreen-${participant.userName}`
          );
        }
      }
      
      // In fullscreen top bar: Show camera preview
      if (this.isFullscreen) {
        if (participant.cameraStream) {
          this.safeSetVideoSource(
            this.$refs['remoteCameraPreview-' + sessionId],
            participant.cameraStream,
            `remoteCameraPreview-${participant.userName}`
          );
        }
        
        // Also attach screen preview if they're screen sharing
        if (participant.screenStream) {
          this.safeSetVideoSource(
            this.$refs['remoteScreenPreview-' + sessionId],
            participant.screenStream,
            `remoteScreenPreview-${participant.userName}`
          );
        }
      }
    },
    
    attachStreamToPreview() {
      // Attach all participant streams to preview elements (for fullscreen)
      this.participants.forEach(p => {
        if (p.cameraStream) {
          this.attachParticipantStreams(p.sessionId);
        }
      });
    },

    handleRemoteStreamRemoved(sessionId) {
      console.log('🚪 handleRemoteStreamRemoved called for:', sessionId);
      const participant = this.participants.find(p => p.sessionId === sessionId);
      
      // Let the vault controller know (ends "present this file" if the presenter left)
      this.$emit('peer-left', sessionId.includes('_') ? sessionId.split('_')[0] : sessionId);
      
      if (!participant) {
        console.warn('⚠️ Participant not found for removal:', sessionId);
        // Still clean up tracked peers even if participant not found
        this.connectedPeers.delete(sessionId);
        const userId = sessionId.split('_')[0];
        this.connectedPeers.delete(userId);
        return;
      }
      
      // Always remove the participant completely when this is called
      // This happens when a peer disconnects or leaves
      console.log('🗑️ Removing participant completely:', participant.userName);
      const index = this.participants.findIndex(p => p.sessionId === sessionId);
      if (index !== -1) {
        this.participants.splice(index, 1);
        console.log(`   ✅ Removed participant at index ${index}, ${this.participants.length} remaining`);
      }
      
      // Remove from connected peers so they can reconnect if needed
      this.connectedPeers.delete(sessionId);
      
      // Also remove by userId in case we have it stored that way
      const userId = sessionId.split('_')[0];
      this.connectedPeers.delete(userId);
      
      // Force re-render of video grid after removal
      this.$nextTick(() => {
        this.attachAllStreams();
      });
    },

    handleConnectionState(sessionId, state) {
      console.log('🔗 Connection state for', sessionId, ':', state);
      
      // Extract userId from sessionId
      const userId = sessionId.split('_')[0];
      
      // Show user-friendly notifications for connection issues
      if (state === 'closed') {
        // Ensure participant is removed when the connection closes
        console.log(`   🧹 Cleaning up participant due to connection ${state}`);
        this.handleRemoteStreamRemoved(sessionId);

      } else if (state === 'failed') {
        // The service attempts an ICE restart before tearing down; if recovery
        // fails it calls handleRemoteStreamRemoved via its peer-leave cleanup
        const user = this.activeUsers.find(u => u.user_id === userId);
        const userName = user ? user.user_name : 'peer';
        console.log(`   ⚠️ Connection to ${userName} failed - attempting automatic recovery...`);

      } else if (state === 'disconnected') {
        console.log(`   ⚠️ Connection disconnected for ${sessionId}, waiting to see if it recovers...`);
        // Don't immediately remove on disconnected - give it a chance to reconnect
        // handlePeerLeave will be called if it doesn't recover

      } else if (state === 'connected') {
        console.log(`   ✅ Successfully connected to ${sessionId}`);
      }
    },
    
    handleScreenShareStopped() {
      console.log('🛑 Screen share stopped (via browser button)');
      this.isScreenSharing = false;
      // Reattach local streams to show camera instead of screen
      this.$nextTick(() => {
        this.attachLocalStreamToVideoElements();
      });
    },

    // Present-sync data channel relays: the vault controller orchestrates
    // the "present this file" session; this component just forwards events
    handlePresentMessage(peerId, userId, message) {
      this.$emit('present-message', { peerId: peerId, userId: userId, message: message });
    },

    handlePresentChannelOpen(peerId, userId) {
      this.$emit('present-channel-open', { peerId: peerId, userId: userId });
    },

    toggleMinimize() {
      this.isMinimized = !this.isMinimized;
      if (this.isMinimized) {
        this.showSettings = false;
      }
      // Watcher will handle stream attachment
    },

    async toggleSettings() {
      this.showSettings = !this.showSettings;
      if (this.showSettings) {
        await this.loadDevices();
      }
    },

    toggleFullscreen() {
      // Note: We let handleFullscreenChange() handle state updates
      // Just request the browser action here
      const container = this.$el;

      if (!this.isFullscreen) {
        // Enter fullscreen
        // Auto-focus will happen in handleFullscreenChange
        if (container.requestFullscreen) {
          container.requestFullscreen();
        } else if (container.webkitRequestFullscreen) {
          container.webkitRequestFullscreen();
        } else if (container.msRequestFullscreen) {
          container.msRequestFullscreen();
        } else {
          // iOS Safari has no element Fullscreen API (only <video> supports it),
          // so the native calls above are no-ops and nothing happens on tap.
          // Fall back to a CSS-only fullscreen: the .fullscreen class already
          // pins the container to the viewport. handleFullscreenChange won't
          // fire for this, so we drive the state directly.
          this.enterPseudoFullscreen();
        }
      } else {
        // Exit fullscreen
        // focusedVideo will be cleared in handleFullscreenChange
        if (this.pseudoFullscreen) {
          this.exitPseudoFullscreen();
        } else if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }
      }
    },

    enterPseudoFullscreen() {
      this.pseudoFullscreen = true;
      this.isFullscreen = true; // isFullscreen watcher reattaches streams
      this.autoFocusVideo();
    },

    exitPseudoFullscreen() {
      this.pseudoFullscreen = false;
      this.isFullscreen = false;
      this.focusedVideo = null;
    },

    handleFullscreenChange() {
      // Update state based on actual fullscreen status
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );

      if (this.isFullscreen !== isCurrentlyFullscreen) {
        const wasFullscreen = this.isFullscreen;
        this.isFullscreen = isCurrentlyFullscreen;
        
        if (this.isFullscreen) {
          // Entering fullscreen - auto-focus a video
          this.autoFocusVideo();
        } else {
          // Exiting fullscreen - clear focus and wait for DOM to settle
          this.focusedVideo = null;
        }
        
        // Note: isFullscreen watcher will handle stream reattachment
      }
    },

    focusVideo(type, sessionId, streamType) {
      console.log(`Focusing video: type=${type}, sessionId=${sessionId}, streamType=${streamType}`);
      this.focusedVideo = {
        type: type, // 'local' or 'remote'
        sessionId: sessionId, // null for local, sessionId for remote
        streamType: streamType // 'camera' or 'screen'
      };
      
      // Re-attach streams after focus changes
      this.$nextTick(() => {
        this.attachAllStreams();
      });
    },
    
    getFocusedParticipantName() {
      if (!this.focusedVideo || this.focusedVideo.type !== 'remote') {
        return '';
      }
      const participant = this.participants.find(p => p.sessionId === this.focusedVideo.sessionId);
      return participant ? participant.userName : 'Unknown';
    },
    
    autoFocusVideo() {
      // Auto-focus logic priority:
      // 1. First remote screen share
      // 2. First remote camera
      // 3. Local camera
      
      // Check for remote screen shares first
      const screenSharer = this.participants.find(p => p.screenStream);
      if (screenSharer) {
        this.focusVideo('remote', screenSharer.sessionId, 'screen');
        return;
      }
      
      // Check for remote cameras
      const cameraUser = this.participants.find(p => p.cameraStream);
      if (cameraUser) {
        this.focusVideo('remote', cameraUser.sessionId, 'camera');
        return;
      }
      
      // Default to local camera
      this.focusVideo('local', null, 'camera');
    },

    async loadDevices() {
      const devices = await webrtcService.getMediaDevices();
      this.audioDevices = devices.audio;
      this.videoDevices = devices.video;
      this.audioOutputDevices = devices.audioOutput;
      
      // Try to set selected devices based on current stream if not already set
      if (this.localStream) {
        const audioTracks = this.localStream.getAudioTracks();
        const videoTracks = this.localStream.getVideoTracks();
        
        if (audioTracks.length > 0) {
           const settings = audioTracks[0].getSettings();
           if (settings.deviceId) this.selectedAudioDevice = settings.deviceId;
        }
        if (videoTracks.length > 0) {
           const settings = videoTracks[0].getSettings();
           if (settings.deviceId) this.selectedVideoDevice = settings.deviceId;
        }
      }
      
      // Fallback if not set
      if (!this.selectedAudioDevice && this.audioDevices.length > 0) {
        this.selectedAudioDevice = this.audioDevices[0].deviceId;
      }
      if (!this.selectedVideoDevice && this.videoDevices.length > 0) {
        this.selectedVideoDevice = this.videoDevices[0].deviceId;
      }
      if (!this.selectedAudioOutputDevice && this.audioOutputDevices.length > 0) {
        this.selectedAudioOutputDevice = this.audioOutputDevices[0].deviceId;
      }
    },

    async changeAudioDevice() {
      if (!this.selectedAudioDevice) return;
      
      try {
        // If we have a local stream, switch the device
        if (this.localStream) {
          const newStream = await webrtcService.switchMediaDevice('audio', this.selectedAudioDevice);
          this.localStream = newStream;
          this.updateLocalVideo();
          console.log('✅ Successfully switched audio device');
        } else {
          // If we don't have a local stream (e.g., device was in use), try to start it now
          console.log('🔄 No local stream - attempting to start with new audio device...');
          await this.retryLocalStream();
        }
      } catch (error) {
        console.error('Error switching audio device:', error);
        this.$emit('show-error', `Failed to switch microphone: ${error.message}`);
      }
    },

    async changeVideoDevice() {
      if (!this.selectedVideoDevice) return;
      
      try {
        // If we have a local stream, switch the device
        if (this.localStream) {
          const newStream = await webrtcService.switchMediaDevice('video', this.selectedVideoDevice);
          this.localStream = newStream;
          this.updateLocalVideo();
          console.log('✅ Successfully switched video device');
        } else {
          // If we don't have a local stream (e.g., device was in use), try to start it now
          console.log('🔄 No local stream - attempting to start with new video device...');
          await this.retryLocalStream();
        }
      } catch (error) {
        console.error('Error switching video device:', error);
        this.$emit('show-error', `Failed to switch camera: ${error.message}`);
      }
    },

    async retryLocalStream() {
      try {
        console.log('🔄 Retrying local stream with selected devices:', {
          audio: this.selectedAudioDevice,
          video: this.selectedVideoDevice
        });
        
        this.localMediaError = null;
        const oldStream = this.localStream;
        this.localStream = await webrtcService.startLocalStream(this.selectedAudioDevice, this.selectedVideoDevice);
        
        // Unmute audio and video since we successfully recovered
        const hasAudio = this.localStream.getAudioTracks().length > 0;
        const hasVideo = this.localStream.getVideoTracks().length > 0;
        
        if (hasAudio && this.audioMuted) {
          console.log('🔊 Auto-unmuting audio after successful device recovery');
          this.audioMuted = false;
          this.localStream.getAudioTracks().forEach(track => track.enabled = true);
        }
        if (hasVideo && this.videoMuted) {
          console.log('📹 Auto-unmuting video after successful device recovery');
          this.videoMuted = false;
          this.localStream.getVideoTracks().forEach(track => track.enabled = true);
        }
        
        // Update video elements
        await this.$nextTick();
        this.attachLocalStreamToVideoElements();
        
        // Now connect to any peers if we weren't connected before
        if (!this.isLocalStreamReady) {
          this.isLocalStreamReady = true;
          
          const mySessionId = webrtcService.getSessionId();
          const otherSessions = this.activeUsers.filter(u => u.session_id && u.session_id !== mySessionId);
          
          for (const session of otherSessions) {
            if (!this.connectedPeers.has(session.user_id)) {
              if (!this.shouldInitiateConnectionTo(session.session_id)) {
                console.log('📞 Waiting for offer from peer after stream recovery (they initiate):', session.user_id);
                continue;
              }
              console.log('📞 Connecting to peer after stream recovery:', session.user_id);
              this.connectedPeers.add(session.user_id);
              await webrtcService.connectToPeer(session.user_id);
            }
          }
        } else {
          // If we already have connections, update the tracks
          console.log('🔄 Updating tracks for existing peer connections...');
          // The switchMediaDevice should handle this, but if we're starting fresh, add tracks
          const audioTrack = this.localStream.getAudioTracks()[0];
          const videoTrack = this.localStream.getVideoTracks()[0];
          
          // Update tracks in all peer connections
          webrtcService.peerConnections.forEach((pc, peerId) => {
            console.log(`  Updating tracks for peer ${peerId}`);
            
            // Replace or add audio track
            if (audioTrack) {
              const audioSender = pc.getSenders().find(s => s.track && s.track.kind === 'audio');
              if (audioSender) {
                audioSender.replaceTrack(audioTrack);
              } else {
                pc.addTrack(audioTrack, this.localStream);
              }
            }
            
            // Replace or add video track
            if (videoTrack) {
              const videoSender = pc.getSenders().find(s => s.track && s.track.kind === 'video');
              if (videoSender) {
                videoSender.replaceTrack(videoTrack);
              } else {
                pc.addTrack(videoTrack, this.localStream);
              }
            }
          });
        }
        
        console.log('✅ Successfully recovered local stream!');
        this.$emit('show-error', 'Camera/microphone connected successfully!');
        
      } catch (error) {
        console.error('❌ Failed to retry local stream:', error);
        this.localMediaError = 'Device unavailable';
        
        let errorMessage = 'Still unable to access camera/microphone.';
        if (error.name === 'NotReadableError') {
          errorMessage = 'Device is still in use by another application.';
        } else if (error.name === 'NotAllowedError') {
          errorMessage = 'Camera/microphone access denied.';
        } else if (error.name === 'NotFoundError') {
          errorMessage = 'Selected device not found.';
        }
        
        this.$emit('show-error', errorMessage);
      }
    },

    async changeAudioOutputDevice() {
      if (this.selectedAudioOutputDevice) {
        try {
          const result = await this.setAudioOutputForAllVideos(this.selectedAudioOutputDevice);
          
          if (result.successCount > 0) {
            console.log(`✅ Successfully changed audio output device for ${result.successCount} element(s)`);
          }
          
          // If some elements were busy, schedule a retry
          if (result.retryNeeded) {
            console.log('⏱️ Some elements were busy, will retry in 1 second...');
            setTimeout(() => {
              this.setAudioOutputForAllVideos(this.selectedAudioOutputDevice).catch(err => {
                console.warn('Retry failed:', err.message);
              });
            }, 1000);
          }
        } catch (error) {
          console.error('Error switching audio output device:', error);
          this.$emit('show-error', 'Failed to change speaker. Your browser may not support this feature.');
        }
      }
    },

    async setAudioOutputForAllVideos(deviceId) {
      console.log(`🔊 Setting audio output to device: ${deviceId}`);
      
      // Collect ONLY remote video elements
      // Local elements are always muted to prevent feedback
      const videoElements = [];
      
      // Add remote video elements
      this.participants.forEach(participant => {
        const remoteVideoRef = this.$refs['remoteVideo-' + participant.sessionId];
        if (remoteVideoRef) {
          const element = Array.isArray(remoteVideoRef) ? remoteVideoRef[0] : remoteVideoRef;
          if (element && element.srcObject) {
            videoElements.push({ element, name: `remoteVideo-${participant.userName}` });
          }
        }
        
        const remoteCameraPreviewRef = this.$refs['remoteCameraPreview-' + participant.sessionId];
        if (remoteCameraPreviewRef) {
          const element = Array.isArray(remoteCameraPreviewRef) ? remoteCameraPreviewRef[0] : remoteCameraPreviewRef;
          if (element && element.srcObject) {
            videoElements.push({ element, name: `cameraPreview-${participant.userName}` });
          }
        }
        
        const remoteScreenPreviewRef = this.$refs['remoteScreenPreview-' + participant.sessionId];
        if (remoteScreenPreviewRef) {
          const element = Array.isArray(remoteScreenPreviewRef) ? remoteScreenPreviewRef[0] : remoteScreenPreviewRef;
          if (element && element.srcObject) {
            videoElements.push({ element, name: `screenPreview-${participant.userName}` });
          }
        }
        
        const minimizedParticipantRef = this.$refs['minimizedParticipant-' + participant.sessionId];
        if (minimizedParticipantRef) {
          const element = Array.isArray(minimizedParticipantRef) ? minimizedParticipantRef[0] : minimizedParticipantRef;
          if (element && element.srcObject) {
            videoElements.push({ element, name: `minimized-${participant.userName}` });
          }
        }
      });
      
      console.log(`  Found ${videoElements.length} video element(s) to update`);
      
      // If no elements found, it's not an error - just no participants yet
      if (videoElements.length === 0) {
        console.log('  ℹ️ No remote video elements to update (no participants)');
        return { successCount: 0, skipCount: 0, retryNeeded: false };
      }
      
      // Apply setSinkId to all video elements with better error handling
      let successCount = 0;
      let skipCount = 0;
      let abortErrorCount = 0;
      let hardErrorCount = 0;
      
      for (const { element, name } of videoElements) {
        if (!element || typeof element.setSinkId !== 'function') {
          console.warn(`  ⚠️ ${name} - setSinkId not supported`);
          hardErrorCount++;
          continue;
        }
        
        // Only try if element has loaded enough data
        if (element.readyState < 1) {
          console.log(`  ⏸️ Skipping ${name} - not ready yet (readyState: ${element.readyState})`);
          skipCount++;
          continue;
        }
        
        try {
          await element.setSinkId(deviceId);
          console.log(`  ✅ ${name}`);
          successCount++;
        } catch (err) {
          // Don't throw on AbortError, just log and continue
          if (err.name === 'AbortError') {
            console.log(`  ⏭️ ${name} - element busy, will retry`);
            abortErrorCount++;
          } else {
            console.warn(`  ⚠️ ${name} - ${err.name}: ${err.message}`);
            hardErrorCount++;
          }
        }
      }
      
      console.log(`  Result: ${successCount} succeeded, ${abortErrorCount} busy, ${skipCount} not ready, ${hardErrorCount} failed`);
      
      // Only throw if ALL elements had hard errors (not AbortErrors or "not ready")
      if (successCount === 0 && hardErrorCount > 0 && (abortErrorCount + skipCount) === 0) {
        throw new Error('Could not set audio output for any video elements. Your browser may not support this feature.');
      }
      
      // Return info about whether retry is needed
      return {
        successCount,
        skipCount,
        retryNeeded: abortErrorCount > 0 || skipCount > 0
      };
    },
    
    safeSetVideoSource(videoElement, stream, debugName) {
      if (!videoElement || !stream) return;
      
      // Handle both single elements and arrays (from refs)
      const element = Array.isArray(videoElement) ? videoElement[0] : videoElement;
      if (!element) return;
      
      // Only set if different stream to avoid abort errors
      if (element.srcObject && element.srcObject.id === stream.id) {
        return; // Already set to this stream
      }
      
      // Pause before changing source to avoid abort errors
      if (!element.paused) {
        element.pause();
      }
      
      element.srcObject = stream;
      
      // Determine if this is a local video element (showing our own camera/screen)
      const isLocalElement = debugName && (
        debugName.includes('Local') || 
        debugName.includes('local') ||
        debugName.includes('gridLocalVideo') ||
        debugName.includes('gridLocalScreen') ||
        debugName.includes('minimizedVideo')
      );
      
      if (isLocalElement) {
        // ALWAYS mute local elements to prevent feedback
        // This doesn't affect the audio being sent to peers - that's controlled by track.enabled
        element.muted = true;
        element.volume = 0;
        console.log(`🔇 Local element ${debugName}: force muted to prevent feedback`);
      } else {
        // Remote stream - apply speaker and participant mute settings
        const sessionId = debugName.match(/remoteVideo-(.+)|remoteCameraPreview-(.+)|remoteScreenPreview-(.+)|minimizedParticipant-(.+)/);
        let participantMuted = false;
        let participantVolume = 1.0;
        
        if (sessionId) {
          const participant = this.participants.find(p => debugName.includes(p.sessionId) || debugName.includes(p.userName));
          if (participant) {
            participantMuted = participant.muted || false;
            participantVolume = participant.volume || 1.0;
          }
        }
        
        // Global speaker mute overrides individual participant mute
        element.muted = !this.speakerEnabled || participantMuted;
        element.volume = participantVolume;
        console.log(`🔊 Remote element ${debugName}: muted=${element.muted}, volume=${element.volume}, speakerEnabled=${this.speakerEnabled}`);
      }
      
      // Start playing the video
      element.play().then(() => {
        console.log(`▶️ Playing ${debugName} with audio`);
        // Clear blocked flag if it was set
        if (this.audioPlaybackBlocked) {
          this.audioPlaybackBlocked = false;
        }
      }).catch(err => {
        // Handle autoplay policy issues
        if (err.name === 'NotAllowedError') {
          console.warn(`⚠️ Autoplay blocked for ${debugName} - audio requires user interaction`);
          // Show button to enable audio
          this.audioPlaybackBlocked = true;
        } else if (err.name !== 'AbortError') {
          console.error(`Error playing ${debugName}:`, err);
        }
      });
      
      // Set audio output device for remote elements only
      // Do this lazily to avoid AbortErrors during rapid stream updates
      if (!isLocalElement && this.selectedAudioOutputDevice && typeof element.setSinkId === 'function') {
        // Use a longer delay and be more forgiving of errors
        setTimeout(() => {
          // Check if element is still valid and has a stream
          if (element.srcObject && element.readyState >= 1) {
            element.setSinkId(this.selectedAudioOutputDevice).then(() => {
              console.log(`✅ Set audio output for ${debugName}`);
            }).catch(err => {
              // Only log non-abort errors (AbortErrors are common during setup)
              if (err.name !== 'AbortError') {
                console.warn(`⚠️ Could not set audio output for ${debugName}:`, err.name);
              }
              // If AbortError, schedule a retry
              if (err.name === 'AbortError') {
                setTimeout(() => {
                  if (element.srcObject && element.readyState >= 1) {
                    element.setSinkId(this.selectedAudioOutputDevice).catch(() => {
                      // Silent fail on retry
                    });
                  }
                }, 1000);
              }
            });
          } else {
            // Element not ready, schedule a retry
            setTimeout(() => {
              if (element.srcObject && element.readyState >= 1) {
                element.setSinkId(this.selectedAudioOutputDevice).catch(() => {
                  // Silent fail on retry
                });
              }
            }, 1000);
          }
        }, 300);
      }
    },
    
    attachLocalStreamToVideoElements() {
      // Check if we're still active before trying to attach streams
      if (!this.isActive) {
        console.log('⏸️ Component not active, skipping local stream attachment');
        return;
      }
      
      const cameraStream = webrtcService.localStream;
      const screenStream = webrtcService.screenStream;

      if (!cameraStream && !screenStream) {
        // Only warn if we expect to have a stream (i.e., we're supposedly active with local stream ready)
        if (this.isLocalStreamReady) {
          console.warn('⚠️ No stream to attach to local video elements (but stream should be ready)');
        }
        return;
      }

      // Fullscreen top bar: local camera preview (only exists in fullscreen)
      if (this.isFullscreen && this.$refs.fullscreenLocalVideo && cameraStream) {
        this.safeSetVideoSource(this.$refs.fullscreenLocalVideo, cameraStream, 'fullscreenLocalVideo');
      }
      
      // Fullscreen top bar: local screen preview (only exists in fullscreen when screen sharing)
      if (this.isFullscreen && this.$refs.fullscreenLocalScreenPreview && screenStream) {
        this.safeSetVideoSource(this.$refs.fullscreenLocalScreenPreview, screenStream, 'fullscreenLocalScreenPreview');
      }

      // Grid local camera: Always show camera
      // This exists in non-fullscreen grid, and in fullscreen when focused on local camera
      if (this.$refs.gridLocalVideo && cameraStream) {
        this.safeSetVideoSource(this.$refs.gridLocalVideo, cameraStream, 'gridLocalVideo');
      }
      
      // Grid local screen: only exists when focused on local screen in fullscreen
      if (this.isFullscreen && this.$refs.gridLocalScreen && screenStream) {
        this.safeSetVideoSource(this.$refs.gridLocalScreen, screenStream, 'gridLocalScreen');
      }
    },
    
    attachAllStreams() {
      // Guard against rapid re-attachment
      if (this.isAttachingStreams) {
        console.log('⏸️ Skipping attachAllStreams - already in progress');
        return;
      }
      
      this.isAttachingStreams = true;
      
      // Use nextTick and a delay to batch multiple attachment requests
      this.$nextTick(() => {
        setTimeout(() => {
          try {
            console.log(`🔗 Attaching all streams (fullscreen: ${this.isFullscreen}, minimized: ${this.isMinimized}, participants: ${this.participants.length})`);
            
            // Check if we've lost our local stream unexpectedly
            if (this.isActive && this.isLocalStreamReady && !this.localStream) {
              console.error('❌ Local stream is missing but should be active! Attempting recovery...');
              this.$emit('show-error', 'Camera/microphone connection lost. Please refresh the page.');
            }
            
            // Attach local streams
            this.attachLocalStreamToVideoElements();
            
            // Attach all participant streams (only those with active streams)
            const participantsWithStreams = this.participants.filter(p => p.cameraStream || p.screenStream);
            console.log(`   📊 ${participantsWithStreams.length}/${this.participants.length} participants have streams`);
            
            participantsWithStreams.forEach(participant => {
              this.attachParticipantStreams(participant.sessionId);
            });
            
            // Attach minimized videos if in minimized mode
            if (this.isMinimized) {
              this.attachMinimizedVideos();
            }
          } finally {
            this.isAttachingStreams = false;
          }
        }, 150);
      });
    },
    
    attachMinimizedVideos() {
      // A remote participant's screen share takes priority: show what they're
      // sharing in the minimized window.
      if (this.remoteScreenShareParticipant && !this.isScreenSharing) {
        this.safeSetVideoSource(
          this.$refs.minimizedRemoteScreen,
          this.remoteScreenShareParticipant.screenStream,
          `minimizedRemoteScreen-${this.remoteScreenShareParticipant.userName}`
        );
        return;
      }

      // When screen sharing, show remote participants in minimized view
      if (this.isScreenSharing && this.participants.length > 0) {
        this.participants.forEach(participant => {
          // Show their camera (or screen if they're sharing)
          const streamToShow = participant.cameraStream || participant.screenStream;
          if (streamToShow) {
            this.safeSetVideoSource(
              this.$refs['minimizedParticipant-' + participant.sessionId],
              streamToShow,
              `minimizedParticipant-${participant.userName}`
            );
          }
        });
      } else {
        // Original behavior - show local stream
        const cameraStream = webrtcService.localStream;
        const screenStream = webrtcService.screenStream;
        const streamForMinimized = this.isScreenSharing && screenStream 
          ? screenStream 
          : cameraStream;
        
        if (streamForMinimized) {
          this.safeSetVideoSource(
            this.$refs.minimizedVideo,
            streamForMinimized,
            'minimizedVideo-local'
          );
        }
      }
    },

    // Diagnostic method to check audio status
    async checkAudioStatus() {
      console.log('🔊 ===== Audio Status Check =====');
      
      // Check browser audio output support
      if (typeof HTMLMediaElement.prototype.setSinkId === 'undefined') {
        console.warn('⚠️ Browser does not support setSinkId (speaker selection)');
      } else {
        console.log('✅ Browser supports setSinkId (speaker selection)');
      }
      
      this.participants.forEach(participant => {
        console.log(`\n📍 Participant: ${participant.userName} (${participant.sessionId})`);
        
        // Check camera stream
        if (participant.cameraStream) {
          const audioTracks = participant.cameraStream.getAudioTracks();
          console.log(`  📻 Camera Stream:`, participant.cameraStream.id);
          console.log(`    Audio Tracks: ${audioTracks.length}`);
          audioTracks.forEach((track, i) => {
            console.log(`      Track ${i}:`, {
              id: track.id,
              label: track.label,
              enabled: track.enabled,
              muted: track.muted,
              readyState: track.readyState
            });
          });
          
          // Check video element
          const videoRef = this.$refs['remoteVideo-' + participant.sessionId];
          if (videoRef) {
            const element = Array.isArray(videoRef) ? videoRef[0] : videoRef;
            if (element) {
              console.log(`  📺 Video Element:`, {
                muted: element.muted,
                volume: element.volume,
                paused: element.paused,
                readyState: element.readyState,
                srcObject: element.srcObject ? element.srcObject.id : 'null'
              });
              
              // Try to get current sink ID
              if (typeof element.sinkId !== 'undefined') {
                console.log(`    🔊 Sink ID: "${element.sinkId}" ${element.sinkId === '' ? '(default)' : ''}`);
              }
            } else {
              console.warn('    ⚠️ Video element not found (array element missing)');
            }
          } else {
            console.warn('    ⚠️ Video element ref not found');
          }
        } else {
          console.log(`    ⚠️ No camera stream`);
        }
      });
      
      console.log('\n🔧 Settings:');
      console.log(`  Selected audio output device: ${this.selectedAudioOutputDevice || '(default)'}`);
      
      // List all available audio output devices
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioOutputs = devices.filter(d => d.kind === 'audiooutput');
        console.log(`  Available audio outputs: ${audioOutputs.length}`);
        audioOutputs.forEach((device, i) => {
          const isSelected = device.deviceId === this.selectedAudioOutputDevice;
          console.log(`    ${isSelected ? '✓' : ' '} ${i}: ${device.label || 'Speaker ' + (i + 1)} (${device.deviceId})`);
        });
      } catch (err) {
        console.error('  Failed to enumerate devices:', err);
      }
      
      console.log('\n💡 Tip: If audio is not playing, try:');
      console.log('  1. Click anywhere on the page (browser autoplay policy)');
      console.log('  2. Check system volume and speaker selection');
      console.log('  3. Try a different speaker in Settings > Speaker');
      console.log('  4. Check if the remote user has their microphone enabled');
      console.log('================================\n');
    },

    updateLocalVideo() {
      // Re-attach stream to video elements
      this.attachLocalStreamToVideoElements();
    },

    enableAudioPlayback() {
      console.log('🔊 User clicked to enable audio playback');
      
      // Try to play all remote video elements
      this.participants.forEach(participant => {
        const videoRef = this.$refs['remoteVideo-' + participant.sessionId];
        if (videoRef) {
          const element = Array.isArray(videoRef) ? videoRef[0] : videoRef;
          if (element && element.srcObject) {
            element.play().then(() => {
              console.log(`✅ Audio enabled for ${participant.userName}`);
              this.audioPlaybackBlocked = false;
            }).catch(err => {
              console.error(`Failed to enable audio for ${participant.userName}:`, err);
            });
          }
        }
      });
    },

    toggleParticipantMute(participant) {
      participant.muted = !participant.muted;
      console.log(`${participant.muted ? '🔇 Muted' : '🔊 Unmuted'} ${participant.userName}`);
      
      // Apply mute state to all video elements for this participant
      const videoRefs = [
        this.$refs['remoteVideo-' + participant.sessionId],
        this.$refs['remoteCameraPreview-' + participant.sessionId],
        this.$refs['remoteScreenPreview-' + participant.sessionId],
        this.$refs['minimizedParticipant-' + participant.sessionId]
      ];
      
      videoRefs.forEach(ref => {
        if (ref) {
          const element = Array.isArray(ref) ? ref[0] : ref;
          if (element) {
            // Global speaker mute overrides individual participant mute
            element.muted = !this.speakerEnabled || participant.muted;
          }
        }
      });
    },

    toggleAudio() {
      this.audioEnabled = !this.audioEnabled;
      webrtcService.toggleAudio(this.audioEnabled);
    },

    toggleVideo() {
      this.videoEnabled = !this.videoEnabled;
      webrtcService.toggleVideo(this.videoEnabled);
    },

    toggleSpeaker() {
      this.speakerEnabled = !this.speakerEnabled;
      console.log(`${this.speakerEnabled ? '🔊 Speakers enabled' : '🔇 Speakers muted'}`);
      
      // Apply global mute state to all remote video elements
      this.participants.forEach(participant => {
        const videoRefs = [
          this.$refs['remoteVideo-' + participant.sessionId],
          this.$refs['remoteCameraPreview-' + participant.sessionId],
          this.$refs['remoteScreenPreview-' + participant.sessionId],
          this.$refs['minimizedParticipant-' + participant.sessionId]
        ];
        
        videoRefs.forEach(ref => {
          if (ref) {
            const element = Array.isArray(ref) ? ref[0] : ref;
            if (element) {
              // Global speaker mute overrides individual participant mute
              element.muted = !this.speakerEnabled || participant.muted;
            }
          }
        });
      });
    },

    async toggleScreenShare() {
      try {
        if (this.isScreenSharing) {
          await webrtcService.stopScreenShare();
          this.isScreenSharing = false;
          
          // Restore camera view
          this.attachLocalStreamToVideoElements();
        } else {
          const screenStream = await webrtcService.startScreenShare();
          this.isScreenSharing = true;
          
          // Auto-minimize when screen sharing starts
          if (!this.isMinimized) {
            this.isMinimized = true;
          }
          
          // Update all streams (watcher will handle this but do it immediately too)
          this.$nextTick(() => {
            this.attachAllStreams();
          });
        }
      } catch (error) {
        console.error('Error toggling screen share:', error);
        this.isScreenSharing = false;
        
        // Show user-friendly error message
        let errorMessage = 'Failed to start screen sharing';
        if (error.name === 'NotSupportedError') {
          errorMessage = error.message;
        } else if (error.name === 'NotAllowedError') {
          errorMessage = 'Screen sharing permission denied';
        }
        
        this.$emit('show-error', errorMessage);
        
        // Restore camera view on error
        this.attachLocalStreamToVideoElements();
      }
    },

    // Deterministic initiator election. Without this, both peers call
    // connectToPeer() on each other and each ends up with a duplicate,
    // conflicting RTCPeerConnection: the second offer gets applied as a
    // renegotiation onto the already-connected pc, breaking the media
    // transport (frozen video) and tearing down the SCTP present-sync data
    // channel. To guarantee a single connection per pair, only the peer with
    // the higher session id sends the initial offer; the other waits for it.
    // The service's glare handling still covers the rare truly-simultaneous case.
    shouldInitiateConnectionTo(theirSessionId) {
      const mySessionId = webrtcService.getSessionId();
      if (!mySessionId || !theirSessionId) {
        return true; // Can't compare - fall back to initiating
      }
      return mySessionId > theirSessionId;
    },

    // A participant refreshed the page or rejoined: they now appear under a new
    // session id while we may still be holding their previous (now-dead) session.
    // Tear the stale one down - both the live UI tile and the service-side peer
    // connection - so the duplicate-connection guard won't block the fresh
    // connection and the head-count stays correct.
    // Parse the timestamp embedded in a session id (format: userId_TIMESTAMP_random).
    // Returns 0 for malformed / bare-userId ids so they always count as "oldest".
    sessionTimestamp(sessionId) {
      const parts = (sessionId || '').split('_');
      if (parts.length < 2) {
        return 0;
      }
      const ts = parseInt(parts[parts.length - 2], 10);
      return isNaN(ts) ? 0 : ts;
    },

    replaceStaleSessionsForUser(session) {
      const newTs = this.sessionTimestamp(session.session_id);
      // Supersede only STRICTLY-older sessions of the same user. A refreshed peer
      // keeps the same userId but gets a newer session timestamp; the old session
      // can linger in presence for ~2 min, so we can't rely on it being absent.
      const stale = this.participants.filter(p =>
        p.userId === session.user_id &&
        p.sessionId !== session.session_id &&
        this.sessionTimestamp(p.sessionId) < newTs
      );
      stale.forEach(p => {
        console.log(`🔄 ${p.userName} rejoined as ${session.session_id}; removing superseded session ${p.sessionId}`);
        // Closes the dead pc in the service AND fires onRemoteStreamRemoved, which
        // clears this participant + its connectedPeers entries here.
        webrtcService.handlePeerLeave(p.sessionId);
      });
    },

    async handleNewParticipant(user) {
      // Connect to new participant only if local stream is ready and not already connected
      if (this.isActive && this.isLocalStreamReady && user.user_id !== this.userId) {
        if (!this.connectedPeers.has(user.user_id)) {
          if (!this.shouldInitiateConnectionTo(user.session_id)) {
            console.log('Waiting for offer from peer (they initiate):', user.user_id, user.session_id);
            return;
          }
          console.log('Connecting to new participant:', user.user_id);
          // Track that we've initiated connection to this user (to prevent duplicate connection attempts)
          this.connectedPeers.add(user.user_id);
          try {
            // Send connection offer to user_id (all their sessions will receive it)
            await webrtcService.connectToPeer(user.user_id);
          } catch (error) {
            console.error('Error connecting to peer:', user.user_id, error);
            this.$emit('show-error', `Failed to connect to ${user.user_name || 'participant'}. Please try again.`);
          }
        } else {
          console.log('Already initiated connection to', user.user_id);
        }
      } else if (this.isActive && !this.isLocalStreamReady) {
        console.log('Skipping connection to', user.user_id, '- local stream not ready yet');
      }
    },

    // ===============================
    // Recording Methods
    // ===============================

    /**
     * Create recording stream by capturing the current browser tab
     * This captures exactly what the user sees on screen
     */
    async createBrowserTabStream() {
      try {
        // Request to capture the current browser tab
        const displayStream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            displaySurface: 'browser', // Prefer browser tab
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            frameRate: { ideal: 30 }
          },
          audio: false, // We'll add audio separately
          preferCurrentTab: true // Chrome-specific hint to prefer current tab
        });

        console.log('[Recording] Browser tab stream captured');

        // Create audio context for mixing participant audio
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        this.audioContext = new AudioContext();
        this.audioDestination = this.audioContext.createMediaStreamDestination();

        // Add local audio
        if (this.localStream) {
          const localAudioTracks = this.localStream.getAudioTracks();
          localAudioTracks.forEach(track => {
            if (track.enabled) {
              try {
                const source = this.audioContext.createMediaStreamSource(new MediaStream([track]));
                source.connect(this.audioDestination);
              } catch (error) {
                console.warn('Could not add local audio', error);
              }
            }
          });
        }

        // Add remote participants' audio
        this.participants.forEach(participant => {
          if (participant.cameraStream || participant.screenStream) {
            const streamToShow = participant.screenStream || participant.cameraStream;
            const audioTracks = streamToShow.getAudioTracks();
            audioTracks.forEach(track => {
              if (track.enabled && !participant.muted) {
                try {
                  const source = this.audioContext.createMediaStreamSource(new MediaStream([track]));
                  source.connect(this.audioDestination);
                } catch (error) {
                  console.warn('Could not add audio from', participant.userName, error);
                }
              }
            });
          }
        });

        // Combine video from screen capture with mixed audio
        const compositeStream = new MediaStream();
        displayStream.getVideoTracks().forEach(track => {
          compositeStream.addTrack(track);
          
          // Listen for when user stops sharing via browser UI
          track.onended = () => {
            console.log('[Recording] Screen sharing stopped by user');
            if (this.isRecording) {
              this.stopRecording();
            }
          };
        });
        
        this.audioDestination.stream.getAudioTracks().forEach(track => compositeStream.addTrack(track));

        console.log('[Recording] Composite stream created with', compositeStream.getTracks().length, 'tracks');
        console.log('[Recording] Video tracks:', compositeStream.getVideoTracks().length);
        console.log('[Recording] Audio tracks:', compositeStream.getAudioTracks().length);

        this.compositeStream = compositeStream;
        return compositeStream;
      } catch (error) {
        console.error('[Recording] Failed to capture browser tab:', error);
        throw error;
      }
    },

    /**
     * Create composite video stream from all participants (fallback method)
     * Combines all video elements into a single canvas and mixes audio
     */
    createCompositeStream() {
      // Determine canvas resolution based on participant count
      const totalParticipants = this.participantCount;
      let canvasWidth = 1280;
      let canvasHeight = 720;
      
      if (totalParticipants > 4) {
        canvasWidth = 1920;
        canvasHeight = 1080;
      }

      // Create off-screen canvas
      this.compositeCanvas = document.createElement('canvas');
      this.compositeCanvas.width = canvasWidth;
      this.compositeCanvas.height = canvasHeight;
      this.compositeContext = this.compositeCanvas.getContext('2d');

      // Set background
      this.compositeContext.fillStyle = '#1e1e1e';
      this.compositeContext.fillRect(0, 0, canvasWidth, canvasHeight);

      // Create audio context for mixing
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      this.audioContext = new AudioContext();
      this.audioDestination = this.audioContext.createMediaStreamDestination();

      // Helper function to get all video elements from the DOM
      const getAllVideoElements = () => {
        const elements = [];
        
        console.log('[Recording] Getting video elements...');
        console.log('[Recording] Local stream exists:', !!this.localStream);
        console.log('[Recording] Participants:', this.participants.length);
        
        // Add local video - try multiple refs
        if (this.localStream) {
          const localVideoRefs = [
            this.$refs.gridLocalVideo,
            this.$refs.fullscreenLocalVideo,
            this.$refs.minimizedVideo
          ];
          
          console.log('[Recording] Checking local video refs...');
          for (const ref of localVideoRefs) {
            if (ref) {
              const element = Array.isArray(ref) ? ref[0] : ref;
              console.log('[Recording] Found local ref, readyState:', element ? element.readyState : 'null', 'srcObject:', !!element.srcObject);
              // Accept readyState >= 1 (HAVE_METADATA) to be more lenient
              if (element && element.srcObject && element.readyState >= 1) {
                console.log('[Recording] ✓ Adding local video element');
                elements.push({
                  element: element,
                  label: 'You',
                  stream: this.localStream
                });
                break; // Only add once
              }
            }
          }
          
          // Add local audio
          const localAudioTracks = this.localStream.getAudioTracks();
          localAudioTracks.forEach(track => {
            if (track.enabled) {
              try {
                const source = this.audioContext.createMediaStreamSource(new MediaStream([track]));
                source.connect(this.audioDestination);
              } catch (error) {
                console.warn('Could not add local audio', error);
              }
            }
          });
        }

        // Add remote participants
        console.log('[Recording] Checking', this.participants.length, 'participants...');
        this.participants.forEach(participant => {
          if (participant.cameraStream || participant.screenStream) {
            const streamToShow = participant.screenStream || participant.cameraStream;
            
            // Try to find video element for this participant
            const possibleRefs = [
              'remoteVideo-' + participant.sessionId,
              'remoteCameraPreview-' + participant.sessionId,
              'remoteScreenPreview-' + participant.sessionId,
              'minimizedParticipant-' + participant.sessionId
            ];
            
            console.log('[Recording] Looking for participant:', participant.userName);
            for (const refName of possibleRefs) {
              const videoRef = this.$refs[refName];
              if (videoRef) {
                const element = Array.isArray(videoRef) ? videoRef[0] : videoRef;
                console.log('[Recording] Found ref', refName, 'readyState:', element ? element.readyState : 'null', 'srcObject:', !!element.srcObject);
                // Accept readyState >= 1 (HAVE_METADATA) to be more lenient
                if (element && element.srcObject && element.readyState >= 1) {
                  console.log('[Recording] ✓ Adding remote video element:', participant.userName);
                  elements.push({
                    element: element,
                    label: participant.userName,
                    stream: streamToShow
                  });
                  break; // Only add once per participant
                }
              }
            }
            
            // Add audio tracks from this participant
            const audioTracksFromParticipant = streamToShow.getAudioTracks();
            audioTracksFromParticipant.forEach(track => {
              if (track.enabled && !participant.muted) {
                try {
                  const source = this.audioContext.createMediaStreamSource(new MediaStream([track]));
                  source.connect(this.audioDestination);
                } catch (error) {
                  console.warn('Could not add audio from', participant.userName, error);
                }
              }
            });
          }
        });
        
        console.log('[Recording] Total video elements found:', elements.length);
        return elements;
      };

      // Draw all videos onto canvas
      const drawFrame = () => {
        if (!this.isDrawingComposite) {
          return; // Stop drawing when not recording
        }

        // Clear canvas
        this.compositeContext.fillStyle = '#1e1e1e';
        this.compositeContext.fillRect(0, 0, canvasWidth, canvasHeight);

        // Get current video elements (they may change during the call)
        const videoElements = getAllVideoElements();
        
        if (videoElements.length === 0) {
          // No videos ready yet, draw placeholder text
          this.compositeContext.fillStyle = '#ffffff';
          this.compositeContext.font = '24px Arial';
          this.compositeContext.textAlign = 'center';
          this.compositeContext.fillText('Waiting for video...', canvasWidth / 2, canvasHeight / 2);
          this.compositeAnimationId = requestAnimationFrame(drawFrame);
          return;
        }

        // Calculate grid layout dynamically
        const gridCols = Math.ceil(Math.sqrt(videoElements.length));
        const gridRows = Math.ceil(videoElements.length / gridCols);
        const cellWidth = canvasWidth / gridCols;
        const cellHeight = canvasHeight / gridRows;

        videoElements.forEach((video, index) => {
          const col = index % gridCols;
          const row = Math.floor(index / gridCols);
          const x = col * cellWidth;
          const y = row * cellHeight;

          // Draw video
          if (video.element && video.element.readyState >= 1) {
            try {
              this.compositeContext.drawImage(video.element, x, y, cellWidth, cellHeight);
              
              // Draw label
              this.compositeContext.fillStyle = 'rgba(0, 0, 0, 0.7)';
              this.compositeContext.fillRect(x + 8, y + cellHeight - 32, cellWidth - 16, 24);
              this.compositeContext.fillStyle = '#ffffff';
              this.compositeContext.font = '14px Arial';
              this.compositeContext.fillText(video.label, x + 16, y + cellHeight - 14);
            } catch (error) {
              console.warn('[Recording] Failed to draw video element:', error);
            }
          } else {
            // Draw placeholder for video not ready
            this.compositeContext.fillStyle = '#333';
            this.compositeContext.fillRect(x, y, cellWidth, cellHeight);
            this.compositeContext.fillStyle = '#ffffff';
            this.compositeContext.font = '16px Arial';
            this.compositeContext.textAlign = 'center';
            this.compositeContext.fillText(video.label, x + cellWidth / 2, y + cellHeight / 2);
            this.compositeContext.textAlign = 'left';
          }
        });

        this.compositeAnimationId = requestAnimationFrame(drawFrame);
      };

      // Capture canvas stream at 30 FPS
      const videoStream = this.compositeCanvas.captureStream(30);
      
      if (!videoStream || videoStream.getTracks().length === 0) {
        console.error('Failed to capture canvas stream');
        return null;
      }
      
      console.log('Canvas stream captured:', videoStream.getTracks().length, 'tracks');
      
      // Combine video from canvas with mixed audio
      const compositeStream = new MediaStream();
      videoStream.getVideoTracks().forEach(track => compositeStream.addTrack(track));
      this.audioDestination.stream.getAudioTracks().forEach(track => compositeStream.addTrack(track));

      console.log('Composite stream created with', compositeStream.getTracks().length, 'tracks');
      console.log('Video tracks:', compositeStream.getVideoTracks().length);
      console.log('Audio tracks:', compositeStream.getAudioTracks().length);

      this.compositeStream = compositeStream;
      
      // Start drawing flag and loop AFTER setting up the stream
      this.isDrawingComposite = true;
      drawFrame();
      
      return compositeStream;
    },

    /**
     * Start recording the video call
     */
    async startRecording() {
      try {
        // Check MediaRecorder support
        if (!window.MediaRecorder) {
          throw new Error('MediaRecorder is not supported in this browser');
        }

        // Get vault and org info from parent
        const vaultInfo = this.getVaultInfo();
        if (!vaultInfo) {
          throw new Error('Vault information not available');
        }

        // Ensure Recordings folder exists
        const recordingsFolderId = await this.ensureRecordingsFolderExists(vaultInfo.vaultId);

        // Create recording stream by capturing browser tab
        console.log('[Recording] Requesting browser tab capture...');
        let stream;
        
        try {
          // Try to capture the current browser tab
          stream = await this.createBrowserTabStream();
        } catch (error) {
          console.warn('[Recording] Browser tab capture failed, falling back to canvas composite:', error);
          
          // Fallback to canvas composite if tab capture fails
          await this.$nextTick();
          await new Promise(resolve => setTimeout(resolve, 500));
          stream = this.createCompositeStream();
        }
        
        if (!stream) {
          throw new Error('Failed to create recording stream');
        }
        
        console.log('[Recording] Recording stream created with', stream.getTracks().length, 'tracks');

        // Determine best codec
        const mimeTypes = [
          'video/webm;codecs=vp8,opus',
          'video/webm;codecs=vp9,opus',
          'video/webm;codecs=h264,opus',
          'video/webm'
        ];

        let selectedMimeType = null;
        for (const mimeType of mimeTypes) {
          if (MediaRecorder.isTypeSupported(mimeType)) {
            selectedMimeType = mimeType;
            break;
          }
        }

        if (!selectedMimeType) {
          throw new Error('No supported video codec found');
        }

        console.log('Using codec:', selectedMimeType);

        // Initialize S3 multipart upload
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
        const filename = `Recording_${timestamp}.webm`;
        
        console.log('Initializing S3 multipart upload...');
        const uploadInfo = await this.$parent.initializeRecordingUpload(
          vaultInfo.vaultId,
          vaultInfo.orgId,
          filename
        );

        this.s3Manager = uploadInfo.s3Instance;
        this.recordingUploadId = uploadInfo.uploadId;
        this.recordingFileId = uploadInfo.fileId;
        this.recordingFilePath = uploadInfo.filePath;
        this.uploadedParts = [];
        this.currentPartNumber = 0;
        this.chunkBuffer = [];
        this.bufferSize = 0;
        this.recordingPartsCount = 0;
        this.recordingError = null;

        // Create MediaRecorder with 1-second chunks
        this.mediaRecorder = new MediaRecorder(stream, {
          mimeType: selectedMimeType,
          videoBitsPerSecond: 2500000 // 2.5 Mbps
        });

        // Handle data available (every 1 second)
        this.mediaRecorder.ondataavailable = async (event) => {
          if (event.data && event.data.size > 0) {
            await this.handleRecordingChunk(event.data, vaultInfo, recordingsFolderId, filename);
          }
        };

        // Handle recording stop
        this.mediaRecorder.onstop = async () => {
          await this.finalizeRecording(vaultInfo, recordingsFolderId, filename);
        };

        // Handle errors
        this.mediaRecorder.onerror = (event) => {
          console.error('MediaRecorder error:', event.error);
          this.recordingError = event.error.message;
          this.stopRecording();
        };

        // Start recording with 1-second timeslice
        this.mediaRecorder.start(1000);
        this.isRecording = true;
        this.recordingStartTime = Date.now();
        
        // Start timer
        this.recordingTimer = setInterval(() => {
          this.recordingDuration = Math.floor((Date.now() - this.recordingStartTime) / 1000);
          
          // Warn at 30 minutes
          if (this.recordingDuration === 1800) {
            this.$emit('show-error', 'Recording has been running for 30 minutes. Consider stopping to avoid performance issues.');
          }
          
          // Hard limit at 2 hours
          if (this.recordingDuration >= 7200) {
            this.$emit('show-error', 'Recording has reached maximum duration of 2 hours. Stopping automatically.');
            this.stopRecording();
          }
        }, 1000);

        console.log('Recording started');
        this.$emit('show-error', 'Recording started');

      } catch (error) {
        console.error('Failed to start recording:', error);
        this.cleanupRecording();
        this.$emit('show-error', `Failed to start recording: ${error.message}`);
      }
    },

    /**
     * Handle a recording chunk (buffer and upload when ready)
     */
    async handleRecordingChunk(chunk, vaultInfo, recordingsFolderId, filename) {
      try {
        // Add chunk to buffer
        this.chunkBuffer.push(chunk);
        this.bufferSize += chunk.size;

        // Check if buffer reached 5MB threshold (S3 multipart minimum)
        const FIVE_MB = 5 * 1024 * 1024;
        if (this.bufferSize >= FIVE_MB) {
          await this.uploadBufferedChunks();
        }
      } catch (error) {
        console.error('Error handling recording chunk:', error);
        this.recordingError = error.message;
      }
    },

    /**
     * Upload buffered chunks as a single S3 part
     */
    async uploadBufferedChunks() {
      if (this.chunkBuffer.length === 0) {
        return;
      }

      try {
        // Combine chunks into single Blob
        const combinedBlob = new Blob(this.chunkBuffer, { type: 'video/webm' });
        this.currentPartNumber++;

        console.log(`Uploading part ${this.currentPartNumber} (${(combinedBlob.size / 1024 / 1024).toFixed(2)} MB)...`);

        // Upload part with retry
        const partResult = await this.$parent.uploadRecordingPart(
          this.s3Manager,
          this.recordingFilePath,
          this.recordingUploadId,
          this.currentPartNumber,
          combinedBlob
        );

        this.uploadedParts.push(partResult);
        this.recordingPartsCount++;

        // Clear buffer
        this.chunkBuffer = [];
        this.bufferSize = 0;

        console.log(`Part ${this.currentPartNumber} uploaded successfully`);

      } catch (error) {
        console.error('Failed to upload buffered chunks:', error);
        throw error;
      }
    },

    /**
     * Stop recording
     */
    async stopRecording() {
      if (!this.isRecording) {
        return;
      }

      try {
        console.log('Stopping recording...');
        this.$emit('show-error', 'Stopping recording, please wait...');

        // Stop MediaRecorder (will trigger onstop callback)
        if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
          this.mediaRecorder.stop();
        }

        // Note: Finalization happens in onstop callback

      } catch (error) {
        console.error('Error stopping recording:', error);
        this.$emit('show-error', `Error stopping recording: ${error.message}`);
        this.cleanupRecording();
      }
    },

    /**
     * Finalize recording (called from MediaRecorder.onstop)
     */
    async finalizeRecording(vaultInfo, recordingsFolderId, filename) {
      try {
        // Upload any remaining buffered chunks
        if (this.chunkBuffer.length > 0) {
          await this.uploadBufferedChunks();
        }

        if (this.uploadedParts.length === 0) {
          throw new Error('No parts were uploaded');
        }

        console.log(`Completing multipart upload with ${this.uploadedParts.length} parts...`);

        // Complete S3 multipart upload
        await this.$parent.completeRecordingUpload(
          this.s3Manager,
          this.recordingFilePath,
          this.recordingUploadId,
          this.uploadedParts
        );

        // Calculate file size (approximate)
        const totalSize = this.uploadedParts.reduce((sum, part) => {
          return sum + (this.bufferSize || 0);
        }, 0);

        // Register file in DynamoDB
        await this.$parent.registerRecordingFile(
          this.recordingFileId,
          filename,
          totalSize,
          vaultInfo.vaultId,
          recordingsFolderId,
          vaultInfo.orgId
        );

        console.log('Recording saved successfully');
        this.$emit('show-error', 'Recording saved to Recordings folder');
        
        // Cleanup
        this.cleanupRecording();

      } catch (error) {
        console.error('Failed to finalize recording:', error);
        this.$emit('show-error', `Failed to save recording: ${error.message}`);
        
        // Try to abort the multipart upload
        try {
          if (this.s3Manager && this.recordingUploadId) {
            await this.$parent.abortRecordingUpload(
              this.s3Manager,
              this.recordingFilePath,
              this.recordingUploadId
            );
          }
        } catch (abortError) {
          console.error('Failed to abort upload:', abortError);
        }
        
        this.cleanupRecording();
      }
    },

    /**
     * Cleanup recording state
     */
    cleanupRecording() {
      console.log('[Recording] Cleaning up recording state...');
      
      // Stop drawing (if using canvas composite)
      this.isDrawingComposite = false;

      // Stop timer
      if (this.recordingTimer) {
        clearInterval(this.recordingTimer);
        this.recordingTimer = null;
      }

      // Stop composite stream
      if (this.compositeStream) {
        this.compositeStream.getTracks().forEach(track => {
          console.log('[Recording] Stopping track:', track.kind);
          track.stop();
        });
        this.compositeStream = null;
      }

      // Cancel animation frame (if using canvas composite)
      if (this.compositeAnimationId) {
        cancelAnimationFrame(this.compositeAnimationId);
        this.compositeAnimationId = null;
      }

      // Close audio context
      if (this.audioContext) {
        this.audioContext.close().catch(err => console.warn('[Recording] Error closing audio context:', err));
        this.audioContext = null;
      }

      // Clear state
      this.isRecording = false;
      this.mediaRecorder = null;
      this.s3Manager = null;
      this.uploadedParts = [];
      this.recordingStartTime = null;
      this.recordingFileId = null;
      this.recordingUploadId = null;
      this.recordingFilePath = null;
      this.currentPartNumber = 0;
      this.chunkBuffer = [];
      this.bufferSize = 0;
      this.recordingDuration = 0;
      this.recordingPartsCount = 0;
      this.compositeCanvas = null;
      this.compositeContext = null;
      this.audioDestination = null;
      
      console.log('[Recording] Cleanup complete');
    },

    /**
     * Get vault information from parent component
     */
    getVaultInfo() {
      // This method should be implemented to get vault info from the parent
      // For now, we'll emit an event and the parent should provide this
      if (this.$parent && this.$parent.vault) {
        return {
          vaultId: this.$parent.vault.id,
          orgId: this.$parent.vault.org_id
        };
      }
      return null;
    },

    /**
     * Ensure Recordings folder exists in vault
     */
    async ensureRecordingsFolderExists(vaultId) {
      if (!this.$parent || typeof this.$parent.createNewFolder !== 'function') {
        throw new Error('Cannot access vault files');
      }

      // Match a root-level "Recordings" folder the same way the rest of the vault
      // code does (case-insensitive, falsy parent check) so we reliably reuse it.
      const isRecordingsFolder = (file) =>
        file &&
        file.name &&
        file.name.toLowerCase() === 'recordings' &&
        file.extension &&
        file.extension.toLowerCase() === 'fld' &&
        !file.parent_folder_file_id &&
        file._deleted !== 1;

      // Fast path: check the already-loaded file list.
      let recordingsFolder = (this.$parent.vaultFiles || []).find(isRecordingsFolder);
      if (recordingsFolder) {
        return recordingsFolder.id;
      }

      // The in-memory list can be stale or not yet loaded when recording from a
      // video chat. Re-check against a fresh list from the server before creating
      // one, otherwise a new Recordings folder gets created for every recording.
      if (typeof this.$parent.fetchAllFilePages === 'function') {
        try {
          const freshFiles = await this.$parent.fetchAllFilePages(vaultId, false, [], null);
          const existing = (freshFiles || []).find(isRecordingsFolder);
          if (existing) {
            return existing.id;
          }
        } catch (err) {
          console.warn('Unable to refresh files before creating Recordings folder:', err && err.message ? err.message : err);
        }
      }

      // No existing Recordings folder found anywhere; create one at the vault root.
      console.log('Creating Recordings folder...');
      const folderId = await this.$parent.createNewFolder('Recordings', null);
      return folderId;
    },

    /**
     * Format recording duration for display
     */
    formatRecordingDuration(seconds) {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;
      
      if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
      }
      return `${minutes}:${secs.toString().padStart(2, '0')}`;
    },

    // Diagnostic method for debugging (can be called from browser console)
    showDiagnostics() {
      this.connectionDiagnostics = webrtcService.getConnectionStats();
      console.log('🔍 WebRTC Connection Diagnostics:', this.connectionDiagnostics);
      console.log('📊 Participants:', this.participants.map(p => ({
        name: p.userName,
        sessionId: p.sessionId,
        hasCamera: !!p.cameraStream,
        hasScreen: !!p.screenStream,
        cameraStreamId: p.cameraStream ? p.cameraStream.id : 'none',
        screenStreamId: p.screenStream ? p.screenStream.id : 'none',
        cameraActive: p.cameraStream ? p.cameraStream.active : false,
        screenActive: p.screenStream ? p.screenStream.active : false,
        cameraTracks: p.cameraStream ? p.cameraStream.getTracks().map(t => ({
          kind: t.kind,
          label: t.label,
          enabled: t.enabled,
          readyState: t.readyState
        })) : [],
        screenTracks: p.screenStream ? p.screenStream.getTracks().map(t => ({
          kind: t.kind,
          label: t.label,
          enabled: t.enabled,
          readyState: t.readyState
        })) : []
      })));
      console.log('🔌 Connected Peers:', Array.from(this.connectedPeers));
      console.log('🎥 Local Stream:', this.localStream);
      console.log('👥 Active Users:', this.activeUsers);
      console.log('🖥️ Is Fullscreen:', this.isFullscreen);
      console.log('📺 Is Screen Sharing:', this.isScreenSharing);
      
      // Check refs
      console.log('🎬 Video Element Refs:');
      this.participants.forEach(p => {
        const gridRef = this.$refs['remoteVideo-' + p.sessionId];
        const previewRef = this.$refs['remoteCameraPreview-' + p.sessionId];
        console.log(`  ${p.userName}:`, {
          hasGridRef: !!gridRef,
          hasPreviewRef: !!previewRef,
          gridSrcObject: gridRef && gridRef[0] ? gridRef[0].srcObject : 'none',
          previewSrcObject: previewRef && previewRef[0] ? previewRef[0].srcObject : 'none'
        });
      });
      
      return this.connectionDiagnostics;
    }
  },
  watch: {
    isLocalStreamReady(newValue, oldValue) {
      // When local stream becomes ready, check for users who joined while we were waiting for permissions
      if (newValue && !oldValue && this.isActive) {
        console.log('🔓 Local stream ready - checking for users who joined during permission prompt');
        const mySessionId = webrtcService.getSessionId();
        const otherSessions = this.activeUsers.filter(u => u.session_id && u.session_id !== mySessionId);
        
        otherSessions.forEach(session => {
          // Check if we already have a connection to any session of this user
          const existsInParticipants = this.participants.find(p => p.userId === session.user_id);
          if (!existsInParticipants && !this.connectedPeers.has(session.user_id)) {
            console.log('📞 Connecting to user who joined during permission prompt:', session.user_id);
            this.handleNewParticipant(session);
          }
        });
      }
    },
    activeUsers: {
      handler(newUsers, oldUsers) {
        console.log('👥 activeUsers changed:', {
          newCount: newUsers.length,
          oldCount: oldUsers ? oldUsers.length : 0,
          isActive: this.isActive,
          isLocalStreamReady: this.isLocalStreamReady,
          currentParticipants: this.participants.length
        });
        
        // Only process if we're active AND local stream is ready
        if (this.isActive && this.isLocalStreamReady) {
          // Get our session ID to filter ourselves out
          const mySessionId = webrtcService.getSessionId();
          const activeSessionIds = newUsers.filter(s => s.session_id).map(s => s.session_id);
          const activeUserIds = [...new Set(newUsers.map(s => s.user_id))];
          
          // This app shows one tile per user. A page refresh produces a brand-new
          // session id (with a newer timestamp) while the old session can linger in
          // presence for up to ~2 min. Collapse each user down to their newest
          // session so we never connect to - or trigger off - a stale older one.
          const latestSessionByUser = {};
          newUsers.forEach(session => {
            if (!session.session_id || session.session_id === mySessionId) {
              return;
            }
            const existing = latestSessionByUser[session.user_id];
            if (!existing || this.sessionTimestamp(session.session_id) > this.sessionTimestamp(existing.session_id)) {
              latestSessionByUser[session.user_id] = session;
            }
          });

          Object.keys(latestSessionByUser).forEach(uid => {
            const session = latestSessionByUser[uid];
            // Already connected to this exact (newest) session - nothing to do
            if (this.participants.find(p => p.sessionId === session.session_id)) {
              return;
            }
            // If we still hold an OLDER session for this user, they refreshed -
            // tear the superseded connection down first so the duplicate-connection
            // guard doesn't block the fresh one and no ghost tile is left behind.
            this.replaceStaleSessionsForUser(session);
            // Connect (handleNewParticipant applies the deterministic initiator election).
            // We initiate by user_id; signals reach all of that user's sessions.
            if (!this.connectedPeers.has(session.user_id) &&
                !this.participants.find(p => p.userId === session.user_id)) {
              this.handleNewParticipant(session);
            }
          });

        // Clean up participants whose sessions have left
        // NOTE: Don't be too aggressive here - presence updates can be delayed
        // We rely on the WebRTC connection state callbacks to handle actual disconnections
        // This is just a fallback for cleaning up after confirmed disconnections
        
        console.log('   📋 Active session IDs:', activeSessionIds);
        console.log('   📋 Active user IDs:', activeUserIds);
        console.log('   📋 Current participant sessions:', this.participants.map(p => p.sessionId));
        
        const staleParticipants = this.participants.filter(participant => {
            // Keep participant if their sessionId is in active list
            if (activeSessionIds.indexOf(participant.sessionId) !== -1) {
              return false;
            }
            // Also keep if any session of their userId is active (multi-device support)
            if (activeUserIds.indexOf(participant.userId) !== -1) {
              return false;
            }
            // Only remove if completely gone from presence
            return true;
          });
          
        if (staleParticipants.length > 0) {
          console.log(`🧹 Found ${staleParticipants.length} stale participant(s) to remove`);
          staleParticipants.forEach(staleParticipant => {
            console.log(`   🗑️ Removing stale participant: ${staleParticipant.userName} (${staleParticipant.sessionId})`);
            this.handleRemoteStreamRemoved(staleParticipant.sessionId);
          });
        } else {
          console.log('   ✅ No stale participants to remove');
        }
      } else if (this.isActive && !this.isLocalStreamReady) {
        console.log('   ⏸️ Skipping activeUsers processing - local stream not ready yet');
      }
    },
    deep: true
  },
    isFullscreen(newVal) {
      // When entering/exiting fullscreen, ensure streams are attached
      // Wait for DOM to update, then reattach all streams
      this.$nextTick(() => {
        // Additional delay to ensure DOM is fully ready
        // When exiting fullscreen, the focused elements are destroyed and grid elements are created
        // We need to wait for this to complete before attaching streams
        setTimeout(() => {
          console.log(`♻️ Fullscreen changed to ${newVal}, reattaching all streams`);
          this.attachAllStreams();
        }, 200);
      });
    },
    isScreenSharing(newVal) {
      // When starting/stopping screen share, reattach all streams
      this.attachAllStreams();
    },
    presentingUserId(newVal, oldVal) {
      // When someone else starts presenting a file, auto-minimize so the
      // presented document (shown in the main app behind the call) is visible.
      // Ignore our own presentation and the case where it just clears.
      if (newVal && newVal !== oldVal && newVal !== this.userId && !this.isMinimized) {
        console.log('📉 Auto-minimizing - remote participant started presenting');
        this.isMinimized = true;
        this.showSettings = false;
      }
    },
    isMinimized(newVal) {
      // When minimizing/maximizing, reattach all streams
      if (newVal) {
        this.attachAllStreams();
      }
    },
    participants: {
      deep: true,
      handler(newParticipants) {
        // When participants change, use attachAllStreams which has built-in debouncing
        this.attachAllStreams();
      }
    }
  },
  mounted() {
    // Listen for fullscreen changes to update state
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', this.handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', this.handleFullscreenChange);
  },
  beforeDestroy() {
    // Cleanup recording if active
    if (this.isRecording) {
      this.cleanupRecording();
    }
    this.endCall();
    // Remove fullscreen event listeners
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('mozfullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('MSFullscreenChange', this.handleFullscreenChange);
  }
};
</script>

<style scoped>
.video-chat-container {
  position: fixed;
  background: #1e1e1e;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  z-index: 1000;
  overflow: hidden;
  transition: all 0.3s ease;
}

.video-chat-container.minimized {
  bottom: 20px;
  right: 20px;
  width: 320px;
  height: 240px;
  max-width: 90vw;
}

/* Adjust width when screen sharing with multiple participants */
.video-chat-container.minimized.sharing-with-participants {
  width: 400px;
}

.video-chat-container.expanded {
  bottom: 20px;
  right: 20px;
  width: 640px;
  max-height: 80vh;
}

.video-chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #2a2a2a;
  cursor: pointer;
  user-select: none;
  min-height: 48px;
}

.video-chat-container.minimized .video-chat-header {
  padding: 8px 12px;
  min-height: 40px;
}

.header-content {
  display: flex;
  align-items: center;
  color: white;
  flex: 1;
  gap: 12px;
}

.header-title {
  font-size: 14px;
  font-weight: 500;
}

.screen-share-indicator {
  display: flex;
  align-items: center;
  background: rgba(33, 150, 243, 0.15);
  padding: 4px 12px;
  border-radius: 16px;
  margin-left: 8px;
  border: 1px solid rgba(33, 150, 243, 0.3);
}

.screen-share-indicator .share-text {
  font-size: 12px;
  color: #2196f3;
  font-weight: 500;
}

.screen-share-indicator .stop-share-btn {
  font-size: 11px;
  height: 20px;
  padding: 0 8px;
  min-width: auto;
}

.recording-indicator {
  display: flex;
  align-items: center;
  background: rgba(211, 47, 47, 0.15);
  padding: 4px 12px;
  border-radius: 16px;
  margin-left: 8px;
  border: 1px solid rgba(211, 47, 47, 0.3);
  gap: 8px;
}

.recording-dot {
  width: 8px;
  height: 8px;
  background: #d32f2f;
  border-radius: 50%;
  animation: recording-pulse 1.5s ease-in-out infinite;
}

@keyframes recording-pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.2);
  }
}

.recording-text {
  font-size: 12px;
  color: #d32f2f;
  font-weight: 600;
  font-family: monospace;
}

.recording-parts {
  font-size: 10px;
  color: #d32f2f;
  opacity: 0.7;
}

/* Minimized mode adjustments for screen share indicator */
.video-chat-container.minimized .screen-share-indicator {
  padding: 3px 8px;
  margin-left: 4px;
}

.video-chat-container.minimized .screen-share-indicator .share-text {
  font-size: 11px;
}

.video-chat-container.minimized .screen-share-indicator .stop-share-btn {
  font-size: 10px;
  height: 18px;
  padding: 0 6px;
  margin-left: 4px;
}

.video-chat-container.minimized .header-title {
  font-size: 13px;
}

.video-chat-container.minimized .header-content {
  gap: 6px;
}

.video-chat-container.minimized .header-content .v-icon {
  font-size: 14px !important;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.video-grid {
  display: grid;
  gap: 8px;
  padding: 12px;
  max-height: 50vh;
  min-height: 200px;
  overflow-y: auto;
}

/* Dynamic grid layout based on participant count */
.video-grid {
  /* 1-2 participants: single column */
  grid-template-columns: 1fr;
}

.video-grid:has(.video-item:nth-child(2)) {
  /* 2 participants: 2 columns */
  grid-template-columns: repeat(2, 1fr);
}

.video-grid:has(.video-item:nth-child(3)) {
  /* 3-4 participants: 2 columns */
  grid-template-columns: repeat(2, 1fr);
}

.video-grid:has(.video-item:nth-child(5)) {
  /* 5-6 participants: 3 columns */
  grid-template-columns: repeat(3, 1fr);
}

.video-grid:has(.video-item:nth-child(7)) {
  /* 7+ participants: 4 columns */
  grid-template-columns: repeat(4, 1fr);
}

.video-item {
  position: relative;
  aspect-ratio: 16 / 9;
  background: #000;
  border-radius: 8px;
  overflow: hidden;
}

.fullscreen-focused-video {
  width: 100%;
  height: 100%;
  border-radius: 0;
  aspect-ratio: auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fullscreen-focused-video .video-element {
  object-fit: contain; /* Show entire video without cropping */
  max-width: 100%;
  max-height: 100%;
}

.fullscreen-focused-video .video-overlay {
  z-index: 10002; /* Ensure name overlay shows above video */
}

.video-grid.fullscreen-focused {
  display: flex;
  grid-template-columns: 1fr;
  padding: 0 !important;
  gap: 0;
  max-height: none;
  min-height: auto;
  overflow: hidden;
  height: 100vh !important;
  align-items: center;
  justify-content: center;
}

.video-element {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-overlay {
  position: absolute;
  bottom: 8px;
  left: 8px;
  right: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  pointer-events: none;
}

.participant-controls {
  display: flex;
  gap: 4px;
  pointer-events: auto;
}

.participant-control-btn {
  background: rgba(0, 0, 0, 0.6) !important;
  backdrop-filter: blur(4px);
}

.participant-control-btn:hover {
  background: rgba(0, 0, 0, 0.8) !important;
}

.participant-name {
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
}

.video-controls {
  display: flex;
  justify-content: center;
  gap: 12px;
  padding: 16px;
  background: #2a2a2a;
}

.control-btn {
  background: rgba(255, 255, 255, 0.1) !important;
}

.control-btn.hang-up {
  background: #d32f2f !important;
}

.minimized-preview {
  width: 100%;
  height: calc(100% - 48px);
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.video-chat-container.minimized .minimized-preview {
  height: calc(100% - 40px);
}

.minimized-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.minimized-screen-video {
  width: 100%;
  height: 100%;
  object-fit: contain; /* show the whole shared screen, not a cropped fill */
  background: #000;
}

.minimized-screen-label {
  position: absolute;
  bottom: 4px;
  left: 4px;
}

.minimized-participants-grid {
  display: flex;
  gap: 4px;
  padding: 4px;
  width: 100%;
  height: 100%;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
}

.minimized-participant-tile {
  position: relative;
  flex: 1;
  min-width: 140px;
  max-width: 100%;
  height: 100%;
  background: #000;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.minimized-participant-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.minimized-participant-name {
  position: absolute;
  bottom: 4px;
  left: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 500;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.video-error-overlay {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: #333;
  color: white;
  text-align: center;
}

.error-text {
  margin-top: 8px;
  font-size: 12px;
  padding: 0 8px;
}

.local-video {
  border: 2px solid #4caf50;
}

.settings-view {
  padding: 20px;
  height: 100%;
  overflow-y: auto;
}

.alert-box {
  background: rgba(255, 152, 0, 0.1);
  border: 1px solid rgba(255, 152, 0, 0.3);
  border-radius: 4px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.alert-text {
  color: #ffa726;
  font-size: 13px;
  font-weight: 500;
}

.alert-subtext {
  color: #bbb;
  font-size: 11px;
  margin: 0;
  margin-top: 4px;
  line-height: 1.4;
}

.device-select {
  width: 100%;
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #444;
  background: #333;
  color: white;
  font-size: 14px;
}

.device-select:focus {
  outline: none;
  border-color: #2196f3;
}

.device-select:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Fullscreen mode */
.video-chat-container.fullscreen {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  height: 100dvh !important; /* dynamic viewport height - excludes mobile browser chrome so nothing is pushed off-screen */
  z-index: 9999 !important;
  border-radius: 0 !important;
  background: #000;
  display: flex;
  flex-direction: column;
}

.video-chat-container.fullscreen .fullscreen-top-bar {
  position: absolute;
  top: calc(20px + env(safe-area-inset-top));
  right: 20px;
  z-index: 10000;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  max-width: 800px;
  justify-content: flex-end;
}

.video-chat-container.fullscreen .video-preview-tile {
  width: 180px;
  height: 101px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  border: 2px solid rgba(255, 255, 255, 0.3);
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  background: #000;
}

.video-chat-container.fullscreen .video-preview-tile .video-element {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-chat-container.fullscreen .video-preview-tile:first-child {
  border-color: #4caf50; /* Your camera gets green border */
}

.video-chat-container.fullscreen .video-preview-tile:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.7);
  border-color: #2196f3;
}

.video-chat-container.fullscreen .video-preview-tile.screen-share-preview {
  border-color: #2196f3; /* Screen shares get blue border */
}

.video-chat-container.fullscreen .video-preview-tile.screen-share-preview:hover {
  border-color: #42a5f5;
}

.video-chat-container.fullscreen .video-preview-tile.no-camera {
  display: none; /* Hide preview if no camera stream */
}

.participant-name-small {
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 500;
}

.video-chat-container.fullscreen .video-grid {
  flex: 1;
  height: calc(100vh - 80px);
  gap: 12px;
  padding: 16px;
  padding-top: 150px; /* Make room for the top bar when showing multiple videos */
  max-height: none;
}

.video-chat-container.fullscreen .video-grid.fullscreen-focused {
  padding-top: 0 !important;
  height: 100vh !important;
}

/* Fullscreen dynamic layout */
.video-chat-container.fullscreen .video-grid {
  grid-template-columns: repeat(2, 1fr);
}

.video-chat-container.fullscreen .video-grid:has(.video-item:nth-child(3)) {
  grid-template-columns: repeat(2, 1fr);
}

.video-chat-container.fullscreen .video-grid:has(.video-item:nth-child(5)) {
  grid-template-columns: repeat(3, 1fr);
}

.video-chat-container.fullscreen .video-grid:has(.video-item:nth-child(7)) {
  grid-template-columns: repeat(4, 1fr);
}

.video-chat-container.fullscreen .video-item {
  border-radius: 8px;
  overflow: hidden;
}

.video-chat-container.fullscreen .video-controls {
  position: absolute;
  bottom: calc(20px + env(safe-area-inset-bottom));
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  border-radius: 25px;
  padding: 12px 20px;
  backdrop-filter: blur(10px);
  z-index: 10001;
}

/* Responsive */
@media (max-width: 768px) {
  /* Pin the expanded (non-fullscreen) panel to the real visible viewport.
     Previously it was anchored to bottom:0 with height:100vh and no top, so on
     mobile (where 100vh is taller than the visible area because of the browser
     chrome) the top of the panel - including the header with the minimize/close
     buttons - was pushed off-screen and unreachable. Using top:0 + dvh + a flex
     column keeps the header pinned at the top and lets the video grid scroll. */
  .video-chat-container.expanded:not(.fullscreen) {
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    height: 100dvh; /* dynamic viewport height - excludes mobile browser chrome */
    max-height: none;
    border-radius: 0;
    display: flex;
    flex-direction: column;
  }

  .video-chat-container.expanded:not(.fullscreen) .video-chat-header {
    flex-shrink: 0;
    padding-top: calc(12px + env(safe-area-inset-top)); /* clear the notch */
  }

  .video-chat-container.expanded:not(.fullscreen) .video-grid {
    flex: 1 1 auto;
    max-height: none;
    min-height: 0; /* allow the grid to shrink so overflow-y:auto can scroll */
  }

  .video-chat-container.expanded:not(.fullscreen) .settings-view {
    flex: 1 1 auto;
    max-height: none;
    min-height: 0;
  }

  .video-chat-container.expanded:not(.fullscreen) .video-controls {
    flex-shrink: 0;
    padding-bottom: calc(12px + env(safe-area-inset-bottom)); /* clear the home indicator */
  }

  .video-chat-container.minimized {
    bottom: 10px;
    right: 10px;
    width: 200px;
    height: 150px;
  }

  .video-chat-container.fullscreen .video-controls {
    bottom: 10px;
    padding: 8px 16px;
  }

  .video-chat-container.fullscreen .fullscreen-top-bar {
    top: calc(10px + env(safe-area-inset-top));
    right: 10px;
  }

  .video-chat-container.fullscreen .local-video-preview {
    width: 180px;
    height: 101px;
  }

  .video-chat-container.fullscreen .video-grid {
    padding-top: 130px; /* Adjusted for mobile */
  }
}
</style>



